class NaturawQuizManager extends HTMLElement {
  constructor() {
    super();
    this.sessionManager = new QuizSessionManager();
    this.session = this.sessionManager.loadSession();
    this.config = window.NaturawQuizConfig || {};
    this.validator = new QuizValidator(this.config);
    this.dynamicPricing = new QuizDynamicPricing(this.config);
    this.translations = {};
    
    // Track purchase options state for double-click functionality
    this.purchaseOptionsShown = false;
    
    // Dynamic step configuration
    this.stepConfig = this.initializeStepConfig();
    this.loadTranslations();
    
    this.init();
  }

  initializeStepConfig() {
    const defaultSteps = [
      { id: 1, name: 'dog_profile', enabled: true, required: true },
      { id: 2, name: 'feeding_style', enabled: true, required: true },
      { id: 3, name: 'dietary_restrictions', enabled: true, required: false },
      { id: 4, name: 'email_collection', enabled: true, required: true },
      { id: 5, name: 'pack_size', enabled: true, required: true },
      { id: 6, name: 'recipe_selection', enabled: true, required: true },
      { id: 7, name: 'treats_selection', enabled: true, required: false },
      { id: 8, name: 'supplements_selection', enabled: true, required: false },
      { id: 9, name: 'results', enabled: true, required: true }
    ];

    // Allow configuration override from quiz config
    if (this.config.steps) {
      return this.config.steps;
    }

    return defaultSteps;
  }

  getActiveSteps() {
    return this.stepConfig.filter(step => step.enabled);
  }

  loadTranslations() {
    const translationScript = document.querySelector('script[data-quiz-translations]');
    if (translationScript) {
      try {
        const content = translationScript.textContent.trim();
        if (content) {
          this.translations = JSON.parse(content);
          console.log('Loaded translations:', this.translations);
        } else {
          console.warn('Empty translation script content');
        }
      } catch (e) {
        console.warn('Failed to load quiz translations:', e);
        console.warn('Translation script content:', translationScript.textContent);
      }
    } else {
      console.warn('Translation script not found');
    }
  }

  getTranslation(key, replacements = {}) {
    const keys = key.split('.');
    let value = this.translations;
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        console.warn(`Translation key not found: ${key}`);
        return key; // Return the key as fallback
      }
    }
    
    // Handle pluralization
    if (typeof value === 'object' && value.one && value.other) {
      const count = replacements.count || 0;
      const template = count === 1 ? value.one : value.other;
      return template.replace('%{count}', count);
    }
    
    // Handle simple string replacements
    if (typeof value === 'string') {
      let result = value;
      for (const [key, replacement] of Object.entries(replacements)) {
        result = result.replace(`%{${key}}`, replacement);
      }
      return result;
    }
    
    return value;
  }

  getTotalSteps() {
    return this.getActiveSteps().length;
  }

  getCurrentStepIndex() {
    const activeSteps = this.getActiveSteps();
    return activeSteps.findIndex(step => step.id === this.session.current_step) + 1;
  }

  getNextActiveStep(currentStep) {
    const activeSteps = this.getActiveSteps();
    const currentIndex = activeSteps.findIndex(step => step.id === currentStep);
    return currentIndex < activeSteps.length - 1 ? activeSteps[currentIndex + 1] : null;
  }

  getPrevActiveStep(currentStep) {
    const activeSteps = this.getActiveSteps();
    const currentIndex = activeSteps.findIndex(step => step.id === currentStep);
    return currentIndex > 0 ? activeSteps[currentIndex - 1] : null;
  }

  connectedCallback() {
    this.render();
    this.updateHeader();
    this.setupEventListeners();
    this.updateQuizContainer();
  }

  init() {
    if (!this.config.pack_sizes) {
      console.error('Quiz configuration not found in window.NaturawQuizConfig');
      return;
    }
    
    console.log('Quiz initialized with config:', this.config);
    console.log('Active steps:', this.getActiveSteps());

  }

  render() {
    const templateId = `quiz-step-${this.session.current_step}`;
    const template = document.getElementById(templateId);
    
    if (!template) {
      console.error(`Template not found: ${templateId}`);
      this.innerHTML = '<div class="quiz__error">Error loading step</div>';
      return;
    }
    
    const clone = template.content.cloneNode(true);
    
    const tempDiv = document.createElement('div');
    tempDiv.appendChild(clone);
    
    // Set the HTML content first
    this.innerHTML = tempDiv.innerHTML + this.renderNavigation();
    
    // Reset purchase options state when rendering a new step
    this.purchaseOptionsShown = false;
    
    // Clear any existing validation messages
    this.clearValidationMessages();
    
    // Update quiz container with current step
    this.updateQuizContainer();
    
    // Then populate form data after DOM is ready
    this.populateFormData();
  }

  updateHeader() {
    const currentStepElement = document.getElementById('current-step');
    const totalStepsElement = document.getElementById('total-steps');
    const progressFillElement = document.getElementById('progress-fill');
    
    const currentStepIndex = this.getCurrentStepIndex();
    const totalSteps = this.getTotalSteps();
    
    if (currentStepElement) {
      currentStepElement.textContent = currentStepIndex;
    }
    
    if (totalStepsElement) {
      totalStepsElement.textContent = totalSteps;
    }
    
    if (progressFillElement) {
      const progressPercentage = (currentStepIndex / totalSteps) * 100;
      progressFillElement.style.width = `${progressPercentage}%`;
    }
  }

  updateQuizContainer() {
    const quizContainer = document.querySelector('.quiz-container');
    if (quizContainer) {
      quizContainer.setAttribute('current-step', this.session.current_step);
    }
  }

  populateFormData() {
    // Use a small delay to ensure DOM is ready
    requestAnimationFrame(() => {
      this.populateStepData(this);
    });
  }

  renderProgressBar() {
    const currentStepIndex = this.getCurrentStepIndex();
    const totalSteps = this.getTotalSteps();
    const progress = (currentStepIndex / totalSteps) * 100;
    
    return `
      <div class="quiz__progress-bar">
        <div class="quiz__progress-fill" style="width: ${progress}%"></div>
      </div>
      <div class="quiz__progress-text">Step ${currentStepIndex} of ${totalSteps}</div>
    `;
  }

  populateStepData(stepElement) {
    // Add debugging to see what's happening
    console.log('Populating step data for step:', this.session.current_step);
    console.log('Session data:', this.session);
    
    switch (this.session.current_step) {
      case 1:
        this.populateDogProfile(stepElement);
        break;
      case 2:
        this.populateFeedingStyle(stepElement);
        break;
      case 3:
        this.populateDietaryRestrictions(stepElement);
        break;
      case 4:
        this.populateEmailCollection(stepElement);
        break;
      case 5:
        console.log('Populating pack size');
        this.populatePackSize(stepElement);
        break;
      case 6:
        this.populateRecipeSelection(stepElement);
        break;
      case 7:
        this.populateTreatsSelection(stepElement);
        break;
      case 8:
        this.populateSupplementsSelection(stepElement);
        break;
      case 9:
        this.populateResultsPage(stepElement);
        break;
    }
  }

  populateDogProfile(stepElement) {
    const nameInput = stepElement.querySelector('#dog-name');
    const ageGroupSelect = stepElement.querySelector('#age-group');
    const puppyRangeSelect = stepElement.querySelector('#puppy-range');
    const puppyRangeContainer = stepElement.querySelector('.puppy-range-container');
    const weightInput = stepElement.querySelector('#dog-weight');
    const dogsList = stepElement.querySelector('#dogs-list');
    const dogsSection = stepElement.querySelector('#dogs-section');
    const formTitle = stepElement.querySelector('#quiz__dog-form-title');
    const addDogBtn = stepElement.querySelector('#add-dog-btn');
    const editDogActions = stepElement.querySelector('#edit-dog-actions');
    
    // Initialize dogs array if it doesn't exist
    if (!this.session.dogs) {
      this.session.dogs = [];
    }
     
    // Update form title based on editing state
    if (this.session.editing_dog_id) {
      if (formTitle) formTitle.textContent = this.getTranslation('step1.edit_dog_details');
    } else {
      if (formTitle) formTitle.textContent = this.getTranslation('step1.add_dog_details');
    }
    
    // Handle editing vs adding mode
    if (this.session.editing_dog_id) {
      // Edit mode - populate form with dog data
      const dog = this.session.dogs.find(d => d.id === this.session.editing_dog_id);
      if (dog && nameInput && ageGroupSelect && weightInput) {
        nameInput.value = dog.name || '';
        ageGroupSelect.value = dog.age_group || '';
        weightInput.value = dog.weight_kg || '';
        
        // Handle puppy range if age group is puppy
        if (dog.age_group === 'puppy' && puppyRangeSelect) {
          puppyRangeSelect.value = dog.puppy_range || '';
          if (puppyRangeContainer) {
            puppyRangeContainer.style.display = 'block';
          }
        }
      }
      
      // Show edit actions, hide add button
      if (addDogBtn) addDogBtn.style.display = 'none';
      if (editDogActions) editDogActions.style.display = 'flex';
      
    } else {
      // Add mode - clear form
      if (nameInput) nameInput.value = '';
      if (ageGroupSelect) ageGroupSelect.value = '';
      if (puppyRangeSelect) {
        puppyRangeSelect.value = '';
        puppyRangeSelect.required = false;
      }
      if (puppyRangeContainer) {
        puppyRangeContainer.style.display = 'none';
      }
      if (weightInput) weightInput.value = '';
      
      // Show add button, hide edit actions
      if (addDogBtn) addDogBtn.style.display = 'inline-flex';
      if (editDogActions) editDogActions.style.display = 'none';
      
      // Update add button state based on form validity
      this.updateAddButtonState();
    }
    
    // Handle dogs list visibility and population
    if (this.session.dogs.length > 0) {
      if (dogsSection) dogsSection.style.display = 'block';
      this.populateDogsListHTML(dogsList);
    } else {
      if (dogsSection) dogsSection.style.display = 'none';
    }
    
    // Add input listeners for form validation
    this.setupFormValidation();
  }

  updateAddButtonState() {
    const addDogBtn = this.querySelector('#add-dog-btn');
    
    if (!addDogBtn) return;
    
    const validation = this.validateDogForm();
    
    // Enable button only when form is valid
    const isValid = validation.isValid;
    addDogBtn.disabled = !isValid;
    addDogBtn.classList.toggle('quiz__btn--disabled', !isValid);
    
    // Show real-time validation errors
    this.showFieldValidationErrors(validation);
  }

  setupFormValidation() {
    const nameInput = this.querySelector('#dog-name');
    const ageGroupSelect = this.querySelector('#age-group');
    const puppyRangeSelect = this.querySelector('#puppy-range');
    const weightInput = this.querySelector('#dog-weight');
    
    // Remove existing listeners to prevent duplicates
    [nameInput, ageGroupSelect, puppyRangeSelect, weightInput].forEach(input => {
      if (input) {
        input.removeEventListener('input', this.updateAddButtonState);
        input.removeEventListener('change', this.updateAddButtonState);
        input.removeEventListener('blur', this.handleFieldBlur);
        
        // Add input/change listener for real-time button state
        if (input.type === 'text' || input.type === 'number') {
          input.addEventListener('input', () => this.updateAddButtonState());
        } else {
          input.addEventListener('change', () => this.updateAddButtonState());
        }
        
        // Add blur listener for validation display
        input.addEventListener('blur', (e) => this.handleFieldBlur(e));
        
        // Mark field as touched on first interaction
        input.addEventListener('focus', () => {
          input.dataset.touched = 'true';
        });
      }
    });
  }

  populateDogsListHTML(dogsList) {
    if (!dogsList) return;
    
    const dogs = this.session.dogs || [];
    
    if (dogs.length === 0) {
      dogsList.innerHTML = '<div class="quiz__no-dogs">No dogs added yet</div>';
      return;
    }
     
    dogsList.innerHTML = dogs.map(dog => `
      <div class="quiz__dog-item" data-dog-id="${dog.id}">
        <div class="quiz__dog-info">
          <div class="quiz__dog-name">${dog.name}</div>
          <div class="quiz__dog-details">Age Group: ${dog.age_group || 'Not specified'}</div>
          ${dog.age_group === 'puppy' && dog.puppy_range ? `<div class="quiz__dog-details">Puppy Range: ${dog.puppy_range}</div>` : ''}
          <div class="quiz__dog-details">Weight: ${dog.weight_kg}kg</div>
        </div>
        <div class="quiz__dog-actions">
          <button type="button" class="quiz__dog-edit-btn btn-reset" data-dog-id="${dog.id}">
           <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M5 19H6.425L16.2 9.225L14.775 7.8L5 17.575V19ZM3 21V16.75L16.2 3.575C16.4 3.39167 16.6208 3.25 16.8625 3.15C17.1042 3.05 17.3583 3 17.625 3C17.8917 3 18.15 3.05 18.4 3.15C18.65 3.25 18.8667 3.4 19.05 3.6L20.425 5C20.625 5.18333 20.7708 5.4 20.8625 5.65C20.9542 5.9 21 6.15 21 6.4C21 6.66667 20.9542 6.92083 20.8625 7.1625C20.7708 7.40417 20.625 7.625 20.425 7.825L7.25 21H3ZM15.475 8.525L14.775 7.8L16.2 9.225L15.475 8.525Z" fill="#18303F"/>
            </svg>
          </button>
          <button type="button" class="quiz__dog-delete-btn btn-reset" data-dog-id="${dog.id}">
           <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M7 21C6.45 21 5.97917 20.8042 5.5875 20.4125C5.19583 20.0208 5 19.55 5 19V6H4V4H9V3H15V4H20V6H19V19C19 19.55 18.8042 20.0208 18.4125 20.4125C18.0208 20.8042 17.55 21 17 21H7ZM9 17H11V8H9V17ZM13 17H15V8H13V17Z" fill="#EF578A"/>
          </svg>
          </button> 
        </div>
      </div>
    `).join('');
  }

  populateFeedingStyle(stepElement) {
    const radioButtons = stepElement.querySelectorAll('input[name="feeding-style"]');
    console.log('Found radio buttons:', radioButtons.length);
    console.log('Looking for feeding style:', this.session.feeding_style);
    
    radioButtons.forEach(radio => {
      const isChecked = radio.value === this.session.feeding_style;
      radio.checked = isChecked;
      console.log(`Radio ${radio.value}: checked = ${isChecked}`);
    });
  }

  populateDietaryRestrictions(stepElement) {
    const checkboxes = stepElement.querySelectorAll('input[name="avoid-ingredients"]');
    console.log('Found checkboxes:', checkboxes.length);
    console.log('Avoided ingredients:', this.session.avoided_ingredients);
    
    checkboxes.forEach(checkbox => {
      const isChecked = this.session.avoided_ingredients.includes(checkbox.value);
      checkbox.checked = isChecked;
      console.log(`Checkbox ${checkbox.value}: checked = ${isChecked}`);
    });

    // Update allergy warning based on current selections
    this.updateAllergyWarning(this.session.avoided_ingredients.length);
    this.updateNoRestrictionsNote(this.session.avoided_ingredients.length);
    
    // Update navigation state and step availability on step load
    this.updateNavigationState();
    this.updateStepAvailability();
  }

  populateEmailCollection(stepElement) {
    const emailInput = stepElement.querySelector('#email-address');
    
    if (emailInput) emailInput.value = this.session.email_address || '';
  }

  populatePackSize(stepElement) {
    const radioButtons = stepElement.querySelectorAll('input[name="pack-size"]');
    console.log('Found pack size radio buttons:', radioButtons.length);
    console.log('Selected pack size:', this.session.selected_pack_size);
    
    radioButtons.forEach(radio => {
      const isChecked = radio.value === this.session.selected_pack_size;
      radio.checked = isChecked;
      console.log(`Pack size ${radio.value}: checked = ${isChecked}`);
    });

    // Update consumption display when pack size is selected
    this.updateConsumptionDisplay(stepElement);
    
    // Update duration display for all pack size options
    this.updatePackSizeDurations(stepElement);
  } 

  calculateTotalDailyConsumption() {
    if (!this.session.dogs || this.session.dogs.length === 0) {
      return { totalGrams: 0, dogConsumptions: [] };
    }

    let totalDailyGrams = 0;
    const dogConsumptions = [];

    this.session.dogs.forEach(dog => {
      if (dog.weight_kg && dog.age_group) {
        const feeding = this.calculateDailyGramsForDog(dog);
        totalDailyGrams += feeding;
        dogConsumptions.push({
          name: dog.name,
          dailyGrams: feeding
        });
      }
    });

    return { totalGrams: totalDailyGrams, dogConsumptions };
  }

  calculateDailyGramsForDog(dog) {
    if (!dog.weight_kg || !dog.age_group) return 0;
    
    let dailyKg = 0;
    
    if (dog.age_group === 'puppy') {
      const puppyRange = dog.puppy_range || '6-9';
      switch (puppyRange) {
        case '0-3': dailyKg = dog.weight_kg * 0.08; break;
        case '3-6': dailyKg = dog.weight_kg * 0.06; break;
        case '6-9': dailyKg = dog.weight_kg * 0.04; break;
        case '9-12': dailyKg = dog.weight_kg * 0.03; break;
        default: dailyKg = dog.weight_kg * 0.04; break;
      }
    } else if (dog.age_group === 'adult') {
      dailyKg = dog.weight_kg * 0.025;
    } else if (dog.age_group === 'senior') {
      dailyKg = dog.weight_kg * 0.02;
    }
    
    return Math.round(dailyKg * 1000);
  }

  updateConsumptionDisplay(stepElement) {
    const consumptionDisplay = stepElement.querySelector('#consumption-display');
    if (!consumptionDisplay) return;

    const consumption = this.calculateTotalDailyConsumption();
    if (consumption.totalGrams === 0) {
      consumptionDisplay.style.display = 'none';
      return;
    }

    // Determine pack weight in grams from available recipe data (prefer explicit unit info)
    let packWeightGrams = 500; // sensible default
    if (window.QuizProductData?.Recipe?.length > 0) {
      const recipes = window.QuizProductData.Recipe;
      let gramsCandidate = null;
      for (const r of recipes) {
        const fv = r.first_available_variant || {};
        const unit = (fv.weight_unit || '').toLowerCase();
        const weightInUnit = fv.weight_in_unit;
        if (typeof weightInUnit === 'number' && weightInUnit > 0) {
          if (unit === 'kg') {
            gramsCandidate = Math.round(weightInUnit * 1000);
          } else if (unit === 'g') {
            gramsCandidate = Math.round(weightInUnit);
          }
        }
        if (!gramsCandidate && typeof r.weight === 'number' && r.weight > 0) {
          gramsCandidate = Math.round(r.weight);
        }
        if (gramsCandidate) break;
      }
      // Accept only reasonable candidate weights; otherwise keep default 500g
      if (gramsCandidate && gramsCandidate >= 200 && gramsCandidate <= 2000) {
        packWeightGrams = gramsCandidate;
      } else {
        packWeightGrams = 500;
      }
    }
    const packFraction = packWeightGrams > 0 ? consumption.totalGrams / packWeightGrams : 0;
    const fractionText = this.formatPackFraction(packFraction);

    // Generate consumption text
    let consumptionText = '';
    if (this.session.dogs.length === 1) {
      const dog = this.session.dogs[0];
      consumptionText = `${dog.name} will consume ${consumption.totalGrams}g (${fractionText} of a pack) per day`;
    } else {
      const dogNames = this.session.dogs.map(dog => dog.name).join(' & ');
      consumptionText = `${dogNames} will consume ${consumption.totalGrams}g (${fractionText} of a pack) per day`;
    }

    consumptionDisplay.innerHTML = `<p class="quiz__consumption-text">${consumptionText}</p>`;
    consumptionDisplay.style.display = 'block';
  }

  updatePackSizeDurations(stepElement) {
    if (!this.session.dogs || this.session.dogs.length === 0) {
      // Hide all duration displays if no dogs
      const durationElements = stepElement.querySelectorAll('.quiz__pack-duration');
      durationElements.forEach(element => {
        element.style.display = 'none';
      });
      return;
    }

    const packSizeOptions = stepElement.querySelectorAll('.quiz__pack-size-option');
    
    packSizeOptions.forEach(option => {
      const packSizeValue = option.dataset.packSize;
      const durationElement = option.querySelector('[data-duration-display]');
      
      if (packSizeValue && durationElement) {
        // Extract numeric value from pack size (e.g., "46 MEALS" -> 46)
        const packSize = parseInt(packSizeValue.replace(' MEALS', ''));
        
        if (!isNaN(packSize)) {
          const duration = this.calculateDuration(packSize, this.session.dogs);
          durationElement.innerHTML = `<span class="quiz__pack-duration-text">This box would last ${duration} days</span>`;
          durationElement.style.display = 'block';
        } else {
          durationElement.style.display = 'none';
        }
      }
    });
  }

  refreshPackSizeDurations() {
    // Only refresh if we're currently on step 5 (pack size selection)
    if (this.session.current_step === 5) {
      const stepElement = this.getCurrentStepElement();
      if (stepElement) {
        this.updatePackSizeDurations(stepElement);
      }
    }
  }
 
  formatPackFraction(fraction) {
    if (fraction >= 1) {
      const wholeNumber = Math.floor(fraction);
      const remainder = fraction - wholeNumber;
      
      const roundedFifth = Math.round(remainder * 5);
      
      if (roundedFifth === 0) {
        return `${wholeNumber}`;
      } else if (roundedFifth === 5) {
        return `${wholeNumber + 1}`;
      } else {
        return `${wholeNumber} + ${roundedFifth}/5`;
      }
    } else if (fraction >= 0.9) {
      return '1';
    } else if (fraction >= 0.7) {
      return '4/5';
    } else if (fraction >= 0.5) {
      return '3/5';
    } else if (fraction >= 0.3) {
      return '2/5';
    } else if (fraction >= 0.1) {
      return '1/5';
    } else {
      return 'less than 1/5';
    }
  } 

  populateRecipeSelection(stepElement) {
    const mealsCounter = stepElement.querySelector('#meals-counter');
    const totalMeals = stepElement.querySelector('#total-meals');
    const mealCounterSuccess = stepElement.querySelector('#meal-counter-success');
    
    // Update meal counter
    if (mealsCounter) mealsCounter.textContent = this.session.total_meals_added || 0;
    
    if (totalMeals) {
      const packSize = this.session.selected_pack_size;
      const mealCount = packSize ? parseInt(packSize.split(' ')[0]) : 46;
      totalMeals.textContent = mealCount;
    }
    
    // Show success message if all meals are selected
    const totalMealsCount = parseInt(totalMeals?.textContent || 46);
    const currentMealsCount = this.session.total_meals_added || 0;
    
    if (currentMealsCount === totalMealsCount && mealCounterSuccess) {
      mealCounterSuccess.style.display = 'flex';
    } else if (mealCounterSuccess) {
      mealCounterSuccess.style.display = 'none';
    }
    
    // Load recipe products
    const recipeGrid = stepElement.querySelector('#recipe-grid');
    if (recipeGrid && window.QuizProductData) {
      this.renderRecipeProducts(recipeGrid);
    }
    
    // Set up random selection button
    const randomSelectionBtn = stepElement.querySelector('#random-selection');
    if (randomSelectionBtn) {
      randomSelectionBtn.addEventListener('click', () => this.handleRandomSelection());
    }
  }

  formatPrice(cents) {
    const moneyFormat = window.theme?.moneyFormat || window.shop?.money_format || window.quizInfo?.money_format || '${{amount}}';
    return formatMoney(cents, moneyFormat);
  }

  renderRecipeProducts(container) {
    const template = document.getElementById('quiz-product-card-template');
    if (!template) return;
    
    const recipeProducts = window.QuizProductData.Recipe || [];
    const filteredRecipes = this.filterProductsByPreferences(recipeProducts, { checkFeedingStyle: true });
    
    container.innerHTML = '';
    
    filteredRecipes.forEach(product => {
      const productCard = template.content.cloneNode(true);
      
      // Populate product data
      const cardElement = productCard.querySelector('.quiz-recipe-cart');
      const image = productCard.querySelector('.quiz-recipe-cart__image');
      const imageLink = productCard.querySelector('.quiz-recipe-cart__image-link');
      const title = productCard.querySelector('.quiz-recipe-cart__title');
      const titleLink = productCard.querySelector('.quiz-recipe-cart__title-link');
      const description = productCard.querySelector('.quiz-recipe-cart__description');
      const quantityDisplay = productCard.querySelector('.quiz-recipe-cart__quantity-display');
      const minusBtn = productCard.querySelector('.quiz-recipe-cart__quantity-btn--minus');
      const plusBtn = productCard.querySelector('.quiz-recipe-cart__quantity-btn--plus');
      const ingredientsBtn = productCard.querySelector('.quiz-recipe-cart__ingredients-btn');
      const price = productCard.querySelector('.quiz-recipe-cart__product-price');
      
      // Set product data 
      cardElement.setAttribute('data-product-id', product.id);
      cardElement.setAttribute('data-product-type', product.type);
      
      // Set card background color from metafield
      const cardBackground = product.metafields?.custom?.card_background || '#EEFFE9';
      cardElement.style.setProperty('--quiz-card-background', cardBackground);
      cardElement.setAttribute('data-card-background', cardBackground);
      console.log(price, 'price');
      console.log(product.price, 'product.price');
      if (image && product.featured_image) {
        image.src = product.featured_image.url + '&width=600';
        image.alt = product.featured_image.alt || product.title;
        image.width = product.featured_image.width || 300;
        image.height = product.featured_image.height || 300;
      }
      
      if (title) title.textContent = product.title.toUpperCase();
      if (price) price.textContent = this.formatPrice(product.price); 
      if (description) {
        const decodedDescription = this.decodeHtmlEntities(product.description || '');
        description.textContent = this.truncateText(decodedDescription, 70);
      }

      if (imageLink && product.url) {
        imageLink.href = product.url;
      }

      if (titleLink && product.url) {
        titleLink.href = product.url;
      }
      
      // Set current quantity
      const currentQuantity = this.getProductQuantity(product.id);
      if (quantityDisplay) {
        // Handle both input and span/div elements
        if (quantityDisplay.tagName === 'INPUT') {
          quantityDisplay.value = currentQuantity;
        } else {
          quantityDisplay.textContent = currentQuantity;
        }
      }
      
            // Set up quantity buttons
      if (minusBtn) {
        minusBtn.addEventListener('click', () => this.updateProductQuantity(product.id, -1));
      }
      if (plusBtn) {
        plusBtn.addEventListener('click', () => this.updateProductQuantity(product.id, 1));
      }

      // Ingredients button is handled by customDelegate in setupEventListeners
      
      container.appendChild(productCard);
    });

    // Update button states after all products are rendered
    setTimeout(() => {
      this.updateQuantityButtonStates();
    }, 0);
  }

  filterProductsByPreferences(products, options = {}) {
    console.log({products, options})
    const { checkFeedingStyle = false } = options;
    const avoided = this.session.avoided_ingredients || [];
    const feedingStyle = this.session.feeding_style;
    const dogs = this.session.dogs || [];
    
    // Determine available life stages based on all dogs
    const availableLifeStages = this.getAvailableLifeStages(dogs);
    


    return (products || []).filter((product) => {
      // Only check feeding_style if it's requested AND the product has this data AND a style is selected
      if (checkFeedingStyle && feedingStyle && Array.isArray(product.feeding_styles) && product.feeding_styles.length > 0) {
        if (!product.feeding_styles.includes(feedingStyle)) {
          return false;
        }
      }

      // Check lifestage compatibility - product should be suitable for at least one of the dogs
      if (availableLifeStages.length > 0 && Array.isArray(product.lifestages) && product.lifestages.length > 0) {
        const normalizedProductLifestages = product.lifestages.map(v => String(v).toLowerCase());
        const hasCompatibleLifestage = availableLifeStages.some(stage => 
          normalizedProductLifestages.includes(stage)
        );
        if (!hasCompatibleLifestage) {
          return false;
        }
      }

      // Check avoided ingredients
      if (avoided.length > 0) {
        const ingredients = Array.isArray(product.ingredients) ? product.ingredients : [];
        const hasAvoided = avoided.some((a) =>
          ingredients.some((ing) => String(ing).toLowerCase().includes(String(a).toLowerCase()))
        );
        if (hasAvoided) {
          return false;
        }
      }

      return true;
    });
  }

  getAvailableLifeStages(dogs) {
    if (!dogs || dogs.length === 0) return [];
    
    const stages = new Set();
    
    dogs.forEach(dog => {
      const ageGroup = dog.age_group;
      if (ageGroup) {
        stages.add(String(ageGroup).toLowerCase());
      }
    });
    
    return Array.from(stages);
  }

  getProductQuantity(productId) {
    const selectedRecipe = this.session.selected_recipes?.find(r => r.product_id === productId);
    return selectedRecipe ? selectedRecipe.quantity : 0;
  }

  getProductInventoryInfo(productId) {
    const allProducts = [
      ...(window.QuizProductData?.Recipe || []),
      ...(window.QuizProductData?.Treats || []),
      ...(window.QuizProductData?.Supplements || [])
    ];
    
    const product = allProducts.find(p => p.id === parseInt(productId));
    if (!product || !product.first_available_variant) {
      return { available: false, inventoryQuantity: 0, inventoryManagement: null };
    }
    
    const variant = product.first_available_variant;
    return {
      available: variant.available || false,
      inventoryQuantity: variant.inventory_quantity || 0,
      inventoryManagement: variant.inventory_management
    };
  }

  filterProductsByAvailability(products) {
    return products.filter(product => {
      const variant = product.first_available_variant;
      if (!variant) return false;
      
      // Always check basic availability first
      if (!variant.available) return false;
      
      // If inventory management is enabled AND we have inventory data, check inventory
      if (variant.inventory_management && typeof variant.inventory_quantity === 'number') {
        return variant.inventory_quantity > 0;
      }
      
      // If no inventory management or no inventory data, assume available
      return true;
    });
  }

  updateProductQuantity(productId, change) {
    if (!this.session.selected_recipes) {
      this.session.selected_recipes = [];
    } 
    
    const existingIndex = this.session.selected_recipes.findIndex(r => r.product_id === productId);
    
    if (existingIndex >= 0) {
      // Update existing recipe and mark as manually edited
      this.session.selected_recipes[existingIndex].quantity += change;
      this.session.selected_recipes[existingIndex].manually_edited = true;
      
      // Remove if quantity becomes 0
      if (this.session.selected_recipes[existingIndex].quantity <= 0) {
        this.session.selected_recipes.splice(existingIndex, 1);
      }
    } else if (change > 0) {
      // Add new recipe and mark as manually edited
      const product = window.QuizProductData.Recipe.find(r => r.id === productId);
      if (product) {
        this.session.selected_recipes.push({
          product_id: productId,
          description: product.description,
          featured_image: product.featured_image,
          variant_id: product.first_available_variant?.id || '',
          quantity: change,
          title: product.title,
          price: product.price,
          collection_id: product.collections[0]?.id || '',
          selling_plan_id: '',
          manually_edited: true
        }); 
      }
    }
    
    // Update total meals count
    this.session.total_meals_added = this.session.selected_recipes.reduce((total, recipe) => total + recipe.quantity, 0);
    
    // Save session
    this.sessionManager.saveSession(this.session);
    
    // Update UI elements without full re-render to prevent page jump
    this.updateQuantityDisplay(productId);
    this.updateMealsCounter();
    this.updateSuccessMessage();
    this.updateQuantityButtonStates();
  }
  
  // Helper method to update just the quantity display for a specific product
  updateQuantityDisplay(productId) {
    const quantityDisplay = this.querySelector(`[data-product-id="${productId}"] .quiz-recipe-cart__quantity-display`);
    if (quantityDisplay) {
      const recipe = this.session.selected_recipes?.find(r => r.product_id === productId);
      const quantity = recipe ? recipe.quantity : 0;
      
      // Handle both input and span/div elements
      if (quantityDisplay.tagName === 'INPUT') {
        quantityDisplay.value = quantity;
      } else {
        quantityDisplay.textContent = quantity;
      }
    }
  }
  
  // Helper method to update the meals counter
  updateMealsCounter() {
    const mealsCounterElement = this.querySelector('#meals-counter');
    const totalMealsElement = this.querySelector('#total-meals');
    
    if (mealsCounterElement) {
      mealsCounterElement.textContent = this.session.total_meals_added || 0;
    }
    
    // Update total meals if needed (this should be calculated based on dogs and pack size)
    if (totalMealsElement && this.session.dogs && this.session.pack_size) {
      const totalMeals = this.calculateTotalMealsNeeded();
      totalMealsElement.textContent = totalMeals;
    }
  }
  
  // Helper method to update success message visibility
  updateSuccessMessage() {
    const successElement = this.querySelector('#meal-counter-success');
    const totalMealsNeeded = this.calculateTotalMealsNeeded();
    const currentMeals = this.session.total_meals_added || 0;
    
    if (successElement) {
      if (currentMeals >= totalMealsNeeded) {
        successElement.style.display = 'flex';
      } else {
        successElement.style.display = 'none';
      }
    }
  }

  // Helper method to calculate total meals needed based on pack size
  calculateTotalMealsNeeded() {
    const packSize = this.session.selected_pack_size;
    return packSize ? parseInt(packSize.split(' ')[0]) : 46;
  }

  // Helper method to update quantity button states based on current totals vs limit
  updateQuantityButtonStates() {
    const totalMealsNeeded = this.calculateTotalMealsNeeded();
    const currentMeals = this.session.total_meals_added || 0;
    const isAtLimit = currentMeals >= totalMealsNeeded;

    // Update plus and minus buttons for each product individually
    this.querySelectorAll('.quiz-recipe-cart').forEach(productCard => {
      const productId = parseInt(productCard.getAttribute('data-product-id'));
      const plusBtn = productCard.querySelector('.quiz-recipe-cart__quantity-btn--plus');
      const minusBtn = productCard.querySelector('.quiz-recipe-cart__quantity-btn--minus');
      
      if (plusBtn) {
        const currentQuantity = this.getProductQuantity(productId);
        const inventoryInfo = this.getProductInventoryInfo(productId);
        
        let shouldDisable = isAtLimit;
        
        // Check if product is not available
        if (!inventoryInfo.available) {
          shouldDisable = true;
        }
        
        // Check inventory limit if inventory management is enabled
        if (inventoryInfo.inventoryManagement && typeof inventoryInfo.inventoryQuantity === 'number') {
          shouldDisable = shouldDisable || currentQuantity >= inventoryInfo.inventoryQuantity;
        }
        
        if (shouldDisable) {
          plusBtn.disabled = true;
          plusBtn.classList.add('quiz-recipe-cart__quantity-btn--disabled');
        } else {
          plusBtn.disabled = false;
          plusBtn.classList.remove('quiz-recipe-cart__quantity-btn--disabled');
        }
      }
      
      if (minusBtn) {
        const currentQuantity = this.getProductQuantity(productId);
        
        if (currentQuantity <= 0) {
          minusBtn.disabled = true;
          minusBtn.classList.add('quiz-recipe-cart__quantity-btn--disabled');
        } else {
          minusBtn.disabled = false;
          minusBtn.classList.remove('quiz-recipe-cart__quantity-btn--disabled');
        }
      }
    });
  }

  showIngredients(productCard, product) {
    const overlay = productCard.querySelector('.quiz-recipe-cart__ingredients-overlay');
    const ingredientsList = overlay.querySelector('.quiz-recipe-cart__ingredients-list');
    
    if (overlay && ingredientsList) {
      const ingredients = product.ingredients_description || product.metafields?.custom.composition || [];
      const ingredientsText = Array.isArray(ingredients) ? ingredients.join(', ') : (ingredients || '');
      
      ingredientsList.textContent = this.decodeHtmlEntities(ingredientsText || '');
      overlay.classList.add('quiz-recipe-cart__ingredients-overlay--active');
    }
  }

  hideIngredients(productCard) {
    const overlay = productCard.querySelector('.quiz-recipe-cart__ingredients-overlay');
    if (overlay) {
      overlay.classList.remove('quiz-recipe-cart__ingredients-overlay--active');
    }
  }

  handleRandomSelection() {
    // Ensure total_meals_added is properly calculated from current selections
    const actualMealsCount = this.session.selected_recipes ? 
      this.session.selected_recipes.reduce((total, recipe) => total + recipe.quantity, 0) : 0;
    this.session.total_meals_added = actualMealsCount;
    
    const totalMealsNeeded = this.calculateTotalMealsNeeded();
    const currentMealsCount = this.session.total_meals_added;
    const remainingMeals = totalMealsNeeded - currentMealsCount;
    
    if (remainingMeals <= 0) return;
    
    const availableRecipes = this.filterProductsByPreferences(window.QuizProductData.Recipe || [], { checkFeedingStyle: true });
    
    if (availableRecipes.length === 0) return;
    
    // DON'T clear current selections - keep manual choices!
    // Initialize selected_recipes if not exists
    if (!this.session.selected_recipes) {
      this.session.selected_recipes = [];
    }
    
    // Determine which products can be modified by Random selection
    const manuallyEditedIds = this.session.selected_recipes
      .filter(recipe => recipe.manually_edited)
      .map(recipe => recipe.product_id);
    
    // Get products that are NOT manually edited (can be modified by Random)
    const availableForRandom = availableRecipes.filter(product => 
      !manuallyEditedIds.includes(product.id)
    );
    
    // Decide which products to use for Random selection
    let productsToModify;
    if (availableForRandom.length > 0) {
      // Use only non-manually-edited products
      productsToModify = availableForRandom;
    } else {
      // All products are manually edited, so we can modify all of them
      productsToModify = availableRecipes;
    }
    
    // Randomly distribute remaining meals among products that can be modified
    let mealsLeft = remainingMeals;
    const minPerProduct = Math.floor(remainingMeals / productsToModify.length);
    const extraMeals = remainingMeals % productsToModify.length;
    
    // Distribute remaining meals to selected products
    productsToModify.forEach((product, index) => {
      if (mealsLeft <= 0) return; // Stop if no meals left to distribute
      
      let quantityToAdd = minPerProduct;
      if (index < extraMeals) quantityToAdd += 1;
      
      if (quantityToAdd <= 0) return;
      
      // Check inventory limitations
      const variant = product.first_available_variant;
      const currentQuantity = this.getProductQuantity(product.id);
      
      if (variant && variant.inventory_management && typeof variant.inventory_quantity === 'number') {
        const availableInventory = variant.inventory_quantity - currentQuantity;
        quantityToAdd = Math.min(quantityToAdd, availableInventory);
      }
      
      if (quantityToAdd <= 0) return;
      
      // Find existing product or create new one
      const existingProductIndex = this.session.selected_recipes.findIndex(item => item.product_id === product.id);
      
      if (existingProductIndex !== -1) {
        // Update existing product quantity (preserve manually_edited flag)
        this.session.selected_recipes[existingProductIndex].quantity += quantityToAdd;
      } else {
        // Add new product (NOT marked as manually_edited since it's from Random)
        this.session.selected_recipes.push({
          product_id: product.id,
          description: product.description,
          featured_image: product.featured_image,
          variant_id: product.first_available_variant?.id || '',
          quantity: quantityToAdd,
          title: product.title,
          price: product.price,
          collection_id: product.collections[0]?.id || '',
          selling_plan_id: '',
          manually_edited: false
        });
      }
      
      mealsLeft -= quantityToAdd;
    });
    
    // Update total meals count by adding the distributed meals
    const mealsAdded = remainingMeals - mealsLeft;
    this.session.total_meals_added = currentMealsCount + mealsAdded;
    this.sessionManager.saveSession(this.session);
    
    // Update UI elements without full re-render to prevent page jump
    this.updateAllQuantityDisplays();
    this.updateMealsCounter();
    this.updateSuccessMessage();
    this.updateQuantityButtonStates();
  }
  
  // Helper method to update all quantity displays after random selection
  updateAllQuantityDisplays() {
    // Update all quantity displays based on current session data
    this.querySelectorAll('.quiz-recipe-cart__quantity-display').forEach(display => {
      const productId = display.closest('[data-product-id]')?.getAttribute('data-product-id');
      if (productId) {
        const recipe = this.session.selected_recipes?.find(r => r.product_id === parseInt(productId));
        const quantity = recipe ? recipe.quantity : 0;
        
        // Handle both input and span/div elements
        if (display.tagName === 'INPUT') {
          display.value = quantity;
        } else {
          display.textContent = quantity;
        }
      }
    });
  }

  populateTreatsSelection(stepElement) {
    const container = stepElement.querySelector('#treats-container');
    if (!container) return;

    const treatsProducts = window.QuizProductData?.Treats || [];
    const filteredTreats = this.filterProductsByPreferences(treatsProducts, { checkFeedingStyle: true });

    if (filteredTreats.length === 0) {
      container.innerHTML = '<p>No treats available</p>';
      return;
    }

    
    const groupedTreats = this.groupProductsByCollections(filteredTreats);

    this.renderDynamicGroups(container, groupedTreats, 'treats');

    this.rerenderNavigation();
  }

  populateSupplementsSelection(stepElement) {
    const container = stepElement.querySelector('#supplements-container');
    if (!container) return;

    const supplementsProducts = window.QuizProductData?.Supplements || [];
    const filteredSupplements = this.filterProductsByPreferences(supplementsProducts, { checkFeedingStyle: true });

    if (filteredSupplements.length === 0) {
      container.innerHTML = '<p>No supplements available</p>';
      return;
    }

    const groupedSupplements = this.groupProductsByCollections(filteredSupplements);
  
    this.renderDynamicGroups(container, groupedSupplements, 'supplements');

    this.rerenderNavigation();
  }

  renderDynamicGroups(container, groupedData, type) {
    
    container.innerHTML = '';
    const { groups, groupOrder } = groupedData;
    groupOrder.forEach((key) => {
      const products = groups[key] || [];
      if (products.length === 0) return;

      const section = document.createElement('div');
      section.className = 'quiz-treats-supplements__section';

      const title = document.createElement('h3');
      title.className = 'quiz-treats-supplements__section-title';
      title.textContent = this.normalizeGroupTitle(key);
      section.appendChild(title);

      const grid = document.createElement('div');
      grid.className = 'quiz-treats-supplements__grid';
      section.appendChild(grid);

      container.appendChild(section);

      this.renderTreatsSupplementsProducts(grid, products, type);
    });
  }

  normalizeGroupTitle(key) {
 
    if (!key || key === 'no-collection') return 'Other';
    return String(key).replace(/\|/g, ' / ');
  }

  populateResults(stepElement) {
    const dogNameResult = stepElement.querySelector('#dog-name-result');
    const dogDetailsResult = stepElement.querySelector('#dog-details-result');
    const recipesResult = stepElement.querySelector('#recipes-summary');
    const treatsResult = stepElement.querySelector('#treats-summary');
    const supplementsResult = stepElement.querySelector('#supplements-summary');
    
    if (dogNameResult) {
      dogNameResult.textContent = `${this.session.dog_profile.name}'s Perfect Meal Plan`;
    }
    
    if (dogDetailsResult) {
      dogDetailsResult.innerHTML = `
        <p>Age: ${this.session.dog_profile.age_years} years</p>
        <p>Weight: ${this.session.dog_profile.weight_kg}kg</p>
        <p>Activity Level: ${this.session.dog_profile.activity_level}</p>
        <p>Feeding Style: ${this.session.feeding_style}</p>
        <p>Pack Size: ${this.session.selected_pack_size}</p>
      `;
    }
    
    if (recipesResult) {
      recipesResult.innerHTML = this.session.selected_recipes.length > 0 
        ? this.session.selected_recipes.map(recipe => `<p>${recipe.quantity}x ${recipe.title}</p>`).join('')
        : '<p>No recipes selected yet</p>';
    }
    
    if (treatsResult) {
      treatsResult.innerHTML = this.session.selected_treats.length > 0 
        ? this.session.selected_treats.map(treat => `<p>${treat.quantity}x ${treat.title}</p>`).join('')
        : '<p>No treats selected</p>';
    }
    
    if (supplementsResult) {
      supplementsResult.innerHTML = this.session.selected_supplements.length > 0 
        ? this.session.selected_supplements.map(supplement => `<p>${supplement.quantity}x ${supplement.title}</p>`).join('')
        : '<p>No supplements selected</p>';
    }
  }

  renderTreatsSupplementsStatusMessage() {
    const currentStep = this.session.current_step;
    
    if (currentStep === 7) {
      // Step 7: Treats Selection
      const selectedTreats = this.session.selected_treats || [];
      const totalCount = selectedTreats.reduce((sum, treat) => sum + treat.quantity, 0);
      
      if (totalCount === 0) {
        return this.getTranslation('step7.empty_message');
      } else {
        return this.getTranslation('step7.treats_selected', { count: totalCount });
      }
    } else if (currentStep === 8) {
      // Step 8: Supplements Selection  
      const selectedSupplements = this.session.selected_supplements || [];
      const totalCount = selectedSupplements.reduce((sum, supplement) => sum + supplement.quantity, 0);
      
      if (totalCount === 0) {
        return this.getTranslation('step8.empty_message');
      } else {
        return this.getTranslation('step8.supplements_selected', { count: totalCount });
      }
    }
    
    return '';
  }

  renderNavigation() {
    const activeSteps = this.getActiveSteps();
    const currentStepIndex = this.getCurrentStepIndex();
    const isFirstStep = currentStepIndex === 1;
    const isLastStep = currentStepIndex === activeSteps.length;
    
    // Special handling for Step 1 - show different UI based on dogs existence
    if (this.session.current_step === 1) {
      const hasDogs = this.session.dogs && this.session.dogs.length > 0;
      
      if (!hasDogs) {
        // Show message when no dogs exist
        return `
          <p class="quiz__nav-message">
            Add your first dog to get started!
          </p>
        `;
      } else {
        // Show navigation buttons when dogs exist
        return `
          <div class="quiz__navigation">
            <button type="button" class="quiz__button quiz__button--back quiz__button--disabled" disabled>Previous</button>
            <button type="button" class="quiz__button quiz__button--next">Next</button>
          </div>
        `;
      }
    }

    // Special handling for Step 7 (Treats Selection) and Step 8 (Supplements Selection)
    if (this.session.current_step === 7 || this.session.current_step === 8) {
      const statusMessage = this.renderTreatsSupplementsStatusMessage();
      const primaryButtonText = this.session.current_step === 7 ? 'Skip' : 'Skip';
      const primaryButtonClass = 'quiz__button--primary';
      
      return `
        <div class="quiz__status-footer">
          <div class="quiz__status-message">
            ${statusMessage}
          </div>
          <div class="quiz__navigation">
            ${!isFirstStep ? '<button type="button" class="quiz__button quiz__button--back">Previous</button>' : ''}
            <button type="button" class="quiz__button ${primaryButtonClass} quiz__button--next">Next</button>
          </div>
        </div>
      `;
    }
    
    // Default navigation for other steps
    return `
      <div class="quiz__navigation">
        ${!isFirstStep && !isLastStep ? '<button type="button" class="quiz__button quiz__button--back">Previous</button>' : ''}
        ${!isLastStep ? '<button type="button" class="quiz__button quiz__button--next">Next</button>' : ''}
        ${isLastStep ? '<button type="button" class="quiz__button quiz__button--complete">Choose Options</button>' : ''}
      </div>
    `;
  }

  rerenderNavigation() {
    const existingNav = this.querySelector('.quiz__navigation, .quiz__nav-message, .quiz__status-footer');
    if (existingNav) {
      existingNav.remove();
    }
    
    const navigationHtml = this.renderNavigation();
    this.insertAdjacentHTML('beforeend', navigationHtml);
  }

  customDelegate(eventName, elementSelector, handler) {
    this.addEventListener(eventName, (e) => {
      const element = e.target.closest(elementSelector);
      if (element) {
        handler.call(element, e, element);
      }
    }, false);
  }

  setupEventListeners() {
    this.customDelegate('click', '.quiz__button--next', () => {
      this.nextStep();
    });

    this.customDelegate('click', '.quiz__button--back', () => {
      this.prevStep();
    });

    this.customDelegate('click', '.quiz__button--complete', () => {
      this.completeQuiz();
    });

    this.customDelegate('click', '.quiz-header__close', () => {
      this.handleCloseQuiz();
    });

    this.customDelegate('click', '#add-dog-btn', () => {
      this.handleAddDog();
    });

    this.customDelegate('click', '#update-dog-btn', () => {
      this.handleUpdateDog();
    });

    this.customDelegate('click', '#cancel-dog-btn', () => {
      this.handleCancelDogEdit();
    });

    this.customDelegate('click', '.quiz__dog-edit-btn', (e, element) => {
      if (element.dataset.dogId) {
        this.handleEditDog(element.dataset.dogId);
      }
    });

    this.customDelegate('click', '.quiz__dog-delete-btn', (e, element) => {
      if (element.dataset.dogId) {
        this.handleDeleteDog(element.dataset.dogId);
      }
    });

    this.customDelegate('click', '.quiz__feeding-style-more-info-btn', (e, element) => {
      this.handleMoreInfoToggle(element);
    });

    this.customDelegate('click', '.quiz__feeding-style-more-info-close', () => {
      this.handleMoreInfoClose();
    });

    this.customDelegate('click', '.quiz__feeding-style-modal-backdrop', () => {
      this.handleMoreInfoClose();
    });

    // Mobile purchase panel event listeners
    this.customDelegate('click', '.quiz-results__purchase-overlay', () => {
      this.hideMobilePurchaseOptions();
    });

    this.customDelegate('click', '.quiz-results__purchase-close', () => {
      this.hideMobilePurchaseOptions();
    });

    // Purchase option block click handlers
    this.customDelegate('click', '.quiz-results__purchase-option', (e, element) => {
      // Don't trigger if click is on radio button, select, or button
      if (e.target.type === 'radio' || e.target.tagName === 'SELECT' || e.target.tagName === 'BUTTON') {
        return;
      }
      
      const radio = element.querySelector('input[name="purchase-option"], input[name="mobile-purchase-option"]');
      if (radio && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    // Ingredients overlay event listeners
    this.customDelegate('click', '.quiz-recipe-cart__ingredients-btn', (e, element) => {
      const productCard = element.closest('.quiz-recipe-cart');
      const productId = productCard?.dataset.productId;
      if (productCard && productId) {
        // Find product data
        const recipes = window.QuizProductData?.Recipe || [];
        const product = recipes.find(p => p.id.toString() === productId);
        if (product) {
          this.showIngredients(productCard, product);
        }
      }
    });

    this.customDelegate('click', '.quiz-recipe-cart__ingredients-close', (e, element) => {
      const productCard = element.closest('.quiz-recipe-cart');
      if (productCard) {
        this.hideIngredients(productCard);
      }
    });

    // Handle form element changes
    this.addEventListener('change', (e) => {
      if (e.target.matches('input[name="feeding-style"]')) {
        this.handleFeedingStyleChange(e.target);
      } else if (e.target.matches('input[name="pack-size"]')) {
        this.handlePackSizeChange(e.target);
      } else if (e.target.matches('input[name="avoid-ingredients"]')) {
        this.handleIngredientChange(e.target);
      } else if (e.target.matches('input[name="email-address"]')) {
        this.session.email_address = e.target.value;
        this.sessionManager.saveSession(this.session);
      } else if (e.target.matches('#age-group')) {
        this.handleAgeGroupChange(e.target);
      } else if (e.target.matches('#puppy-range')) {
        this.handlePuppyRangeChange(e.target);
      }
    });

    this.addEventListener('input', (e) => {
      if (e.target.matches('#email-address')) {
        this.updateEmailAddress(e.target);
      } else if (e.target.matches('input[name="avoid-ingredients"]')) {
        this.updateDietaryRestrictions();
      }
    });
  }

  updateEmailAddress(input) {
    this.session = this.sessionManager.updateSession({ email_address: input.value });
  }

  updateDietaryRestrictions() {
    const checkboxes = document.querySelectorAll('input[name="avoid-ingredients"]:checked');
    const avoided = Array.from(checkboxes).map(cb => cb.value);
    this.session = this.sessionManager.updateSession({ avoided_ingredients: avoided });
    
    // Show/hide allergy warning based on number of selected allergies
    this.updateAllergyWarning(avoided.length);
    this.updateNoRestrictionsNote();
    
    // Update navigation state based on product availability
    this.updateNavigationState();
    
    // Update step availability for treats and supplements
    this.updateStepAvailability();
  }

  updateAllergyWarning(count) {
    const warningElement = document.getElementById('quiz-allergy-warning');
    if (!warningElement) return;
    
    if (count >= 3) {
      warningElement.style.display = 'block';
    } else {
      warningElement.style.display = 'none';
    }
  }

  updateNoRestrictionsNote(passedCount) {
    const stepElement = this.getCurrentStepElement();
    if (!stepElement) return;
    const note = stepElement.querySelector('.quiz__no-restrictions-note');
    if (!note) return;
    const count = typeof passedCount === 'number'
      ? passedCount
      : (this.session.avoided_ingredients ? this.session.avoided_ingredients.length : 0);
    if (count === 0) {
      note.style.display = '';
    } else {
      note.style.display = 'none';
    } 
  }

  checkProductAvailability() {
    const availableRecipes = this.filterProductsByPreferences(window.QuizProductData?.Recipe || [], { checkFeedingStyle: true });
    const availableTreats = this.filterProductsByPreferences(window.QuizProductData?.Treats || [], { checkFeedingStyle: true });
    const availableSupplements = this.filterProductsByPreferences(window.QuizProductData?.Supplements || [], { checkFeedingStyle: true });
    
    return {
      recipes: availableRecipes.length > 0,
      treats: availableTreats.length > 0,
      supplements: availableSupplements.length > 0,
      recipesCount: availableRecipes.length,
      treatsCount: availableTreats.length,
      supplementsCount: availableSupplements.length
    };
  }

  updateNavigationState() {
    const availability = this.checkProductAvailability();
    const nextButton = this.querySelector('.quiz__button--next');
    
    if (!nextButton) return;
    
    // For step 3 (dietary restrictions), block navigation if no recipes available
    if (this.session.current_step === 3 && !availability.recipes) {
      nextButton.disabled = true;
      nextButton.classList.add('quiz__button--disabled');
      nextButton.textContent = this.getTranslation('step6.no_recipes_available');
      
      // Show validation error
      const errorElement = this.querySelector('#quiz-validation-errors');
      const errorText = this.querySelector('#quiz-validation-errors .quiz__error-text');
      if (errorElement && errorText) {
        errorText.textContent = this.getTranslation('step6.no_recipes_error');
        errorElement.style.display = 'block';
      }
    } else {
      nextButton.disabled = false;
      nextButton.classList.remove('quiz__button--disabled');
      nextButton.textContent = this.getTranslation('navigation.next');
      
      // Hide validation error
      const errorElement = this.querySelector('#quiz-validation-errors');
      if (errorElement) {
        errorElement.style.display = 'none';
      }
    }
  }

  updateStepAvailability() {
    const availability = this.checkProductAvailability();
    
    // Update step configuration based on product availability
    const updatedSteps = this.stepConfig.map(step => {
      if (step.name === 'treats_selection') {
        return { ...step, enabled: availability.treats };
      }
      if (step.name === 'supplements_selection') {
        return { ...step, enabled: availability.supplements };
      }
      return step;
    });
    
    this.stepConfig = updatedSteps;
    this.sessionManager.updateStepConfig(updatedSteps);
  }



  handleFeedingStyleChange(input) {
    const feedingStyle = input.value;
    this.session.feeding_style = feedingStyle;
    
    // Clear all selected products when feeding style changes
    this.clearSelectedProducts(true, true, true);
    
    this.session = this.sessionManager.updateSession({ feeding_style: feedingStyle });
  }

  clearSelectedProducts(clearRecipes = true, clearTreats = true, clearSupplements = true) {
    if (clearRecipes) {
      this.session.selected_recipes = [];
      this.session.total_meals_added = 0;
    }
    
    if (clearTreats) {
      this.session.selected_treats = [];
    }
    
    if (clearSupplements) {
      this.session.selected_supplements = [];
    }
    
    // Save the updated session
    this.sessionManager.saveSession(this.session);
    

  }

  handlePackSizeChange(input) {
    const packSize = input.value;
    this.session.selected_pack_size = packSize;
    
    // Reset recipe selections when pack size changes
    this.clearSelectedProducts(true, false, false);
    
    this.session = this.sessionManager.updateSession({ 
      selected_pack_size: packSize
    });
    
    // Update consumption display when pack size changes
    const stepElement = this.getCurrentStepElement();
    if (stepElement) {
      this.updateConsumptionDisplay(stepElement);
    }
    

  }

  handleIngredientChange(input) {
    const ingredient = input.value;
    const isChecked = input.checked;
     
    if (isChecked) {
      if (!this.session.avoided_ingredients.includes(ingredient)) {
        this.session.avoided_ingredients.push(ingredient);
      }
    } else {
      this.session.avoided_ingredients = this.session.avoided_ingredients.filter(i => i !== ingredient);
    }
    
  
      // Clear all selected products when dietary restrictions change
      this.clearSelectedProducts(true, true, true);
  
    
    this.session = this.sessionManager.updateSession({ avoided_ingredients: this.session.avoided_ingredients });
  }

  handleAgeGroupChange(select) {
    const puppyRangeContainer = this.querySelector('.puppy-range-container');
    const puppyRangeSelect = this.querySelector('#puppy-range');

    if (select.value === 'puppy') {
      if (puppyRangeContainer) {
        puppyRangeContainer.style.display = 'block';
      }
      if (puppyRangeSelect) {
        puppyRangeSelect.required = true;
      }
    } else {
      if (puppyRangeContainer) {
        puppyRangeContainer.style.display = 'none';
      }
      if (puppyRangeSelect) {
        puppyRangeSelect.required = false;
        puppyRangeSelect.value = '';
      }
    }

    // Update session with age group selection
    this.session.age_group = select.value;
    this.sessionManager.saveSession(this.session);
  }

  handlePuppyRangeChange(select) {
    // Update session with puppy range selection
    this.session.puppy_range = select.value;
    this.sessionManager.saveSession(this.session);
  }

  async nextStep() {
    if (await this.validateCurrentStep()) {
      this.sessionManager.markStepCompleted(this.session.current_step);
      
      // Show loading page after step 4 before going to step 5
      if (this.session.current_step === 4) {
        this.showLoadingPage();
        this.sendKlaviyoSubscription(this.session.email_address);
        return;
      }
      
      const nextStep = this.getNextActiveStep(this.session.current_step);
      if (nextStep) {
        this.session.current_step = nextStep.id;
        this.session = this.sessionManager.updateSession({ current_step: this.session.current_step });
        this.render();
        this.updateHeader();
      }
    }
  }

  async prevStep() {
    const prevStep = this.getPrevActiveStep(this.session.current_step);
    if (prevStep) {
      this.session.current_step = prevStep.id;
      this.session = this.sessionManager.updateSession({ current_step: this.session.current_step });
      this.render();
      this.updateHeader();
    }
  }

  showLoadingPage() {
    // Render loading template
    const template = document.getElementById('quiz-step-loading');
    if (!template) {
      console.error('Loading template not found');
      this.proceedToStep5();
      return;
    }

    const clone = template.content.cloneNode(true);
    const tempDiv = document.createElement('div');
    tempDiv.appendChild(clone);
    
    this.innerHTML = tempDiv.innerHTML;
    
    // Start loading animation
    this.startLoadingAnimation();
    
    // Auto-proceed to step 5 after 3 seconds (2s animation + 1s hold)
    setTimeout(() => {
      this.proceedToStep5();
    }, 3000);
  }

  startLoadingAnimation() {
    const loadingStep = document.getElementById('quiz-loading-step');
    const percentageElement = document.getElementById('loading-percentage');
    const progressFill = document.getElementById('loading-progress-fill');
    
    if (!loadingStep || !percentageElement || !progressFill) {
      console.error('Loading animation elements not found');
      return;
    }

    // Add animation class
    loadingStep.classList.add('quiz__loading--animate');
    
    // Animate percentage counter from 0 to 88
    let currentPercentage = 0;
    const targetPercentage = 88;
    const duration = 2000; // 2 seconds animation
    const increment = targetPercentage / (duration / 50); // Update every 50ms
    
    const percentageInterval = setInterval(() => {
      currentPercentage += increment;
      if (currentPercentage >= targetPercentage) {
        currentPercentage = targetPercentage;
        clearInterval(percentageInterval);
      }
      percentageElement.textContent = Math.round(currentPercentage) + '%';
    }, 50);
    
    // Animate progress bar
    setTimeout(() => {
      progressFill.style.width = '88%';
      progressFill.style.transition = 'width 2s ease-in-out';
    }, 100);
  }

  proceedToStep5() {
    const nextStep = this.getNextActiveStep(this.session.current_step);
    if (nextStep) {
      this.session.current_step = nextStep.id;
      this.session = this.sessionManager.updateSession({ current_step: this.session.current_step });
      this.render();
      this.updateHeader();
    }
  }

  async validateCurrentStep() {
    this.sessionManager.clearValidationErrors();
    this.clearValidationMessages();
    
    console.log('Validating step:', this.session.current_step);
    console.log('Current session:', this.session);
    
    const validation = this.validator.validateStep(this.session.current_step, this.session);
    
    if (!validation.isValid) {
      // Set validation errors in session
      validation.errors.forEach(error => {
        this.sessionManager.setValidationError(`step_${this.session.current_step}`, error);
      });
      
      this.showValidationMessages();
      return false;
    }
    return true;
  }

  sendKlaviyoSubscription(email) {
    console.log('Klaviyo subscription response:', email);

    const klaviyoSettings = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        g: 'Yc4juN',  // Replace with your list ID
        email: email,
        $fields: 'email',
      }),
    };

    fetch("https://manage.kmail-lists.com/ajax/subscriptions/subscribe", klaviyoSettings)
    .then(response => response.text())
    .then(result => {
      console.log('Klaviyo subscription response:', result);
    })
    .catch(error => {
      console.error('Error subscribing to Klaviyo:', error);
    });
  }


  clearValidationMessages() {
    const errorContainer = this.querySelector('#quiz-validation-errors');
    if (errorContainer) {
      errorContainer.style.display = 'none';
      const errorText = errorContainer.querySelector('.quiz__error-text');
      if (errorText) {
        errorText.textContent = '';
      }
    }
  }

  showValidationMessages() {
    const errorContainer = this.querySelector('#quiz-validation-errors');
    if (!errorContainer) return;
    
    const session = this.sessionManager.getSession();
    const validationErrors = session.validation_errors || {};
    
    // Get all error messages for this step
    const errorMessages = Object.values(validationErrors).filter(error => error);
    
    if (errorMessages.length > 0) {
      const errorText = errorContainer.querySelector('.quiz__error-text');
      if (errorText) {
        // Show the first error message
        errorText.textContent = errorMessages[0];
        errorContainer.style.display = 'block';
        
        // Scroll to the error message
        errorContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }

  validateDogForm() {
    const nameInput = this.querySelector('#dog-name');
    const ageGroupSelect = this.querySelector('#age-group');
    const puppyRangeSelect = this.querySelector('#puppy-range');
    const weightInput = this.querySelector('#dog-weight');
    
    if (!nameInput || !ageGroupSelect || !weightInput) {
      return { isValid: false, errors: ['Form inputs not found'] };
    }
    
    const dogData = {
      name: nameInput.value,
      age_group: ageGroupSelect.value,
      puppy_range: puppyRangeSelect ? puppyRangeSelect.value : '',
      weight: weightInput.value
    };
    
    return this.validator.validateDogProfile(dogData);
  }



  async completeQuiz() {
    // For step 9 (results), handle double-click functionality
    if (this.session.current_step === 9) {
      if (!this.purchaseOptionsShown) {
        // First click: Show purchase options
        this.showMobilePurchaseOptions();
        this.purchaseOptionsShown = true;
        this.updateCompleteButtonText();
        return;
      } else {
        // Second click: Add selected purchase option to cart
        await this.addSelectedPurchaseOptionToCart();
        return;
      }
    }
    
    this.session.is_complete = true;
    this.sessionManager.saveSession(this.session);
    
    console.log('Final quiz session:', this.session);
  }

  updateCompleteButtonText() {
    const completeButton = this.querySelector('.quiz__button--complete');
    if (completeButton && this.purchaseOptionsShown) {
      completeButton.textContent = this.getTranslation('step9.add_to_bag');
    }
  }

  async addSelectedPurchaseOptionToCart() {
    // Get the selected purchase option
    const selectedOption = this.querySelector('input[name="purchase-option"]:checked');
    if (!selectedOption) {
      console.error('No purchase option selected');
      return;
    }

    const purchaseType = selectedOption.value;
    const stepElement = this.querySelector('.quiz__step[data-step="9"]') || this;
    
    // Use the existing addToCart method
    await this.addToCart(purchaseType, stepElement);
  }

  showMobilePurchaseOptions() {

    if (window.innerWidth >= 990) {
      // On desktop, just scroll to purchase options
      const purchaseSection = this.querySelector('.quiz-results__purchase');
      if (purchaseSection) {
        purchaseSection.scrollIntoView({ behavior: 'smooth' });
      }
      return;
    }

    // On mobile, show existing purchase section as slide panel
    const purchaseSection = this.querySelector('.quiz-results__purchase');
    const overlay = this.querySelector('.quiz-results__purchase-overlay');
    
    if (purchaseSection && overlay) {
      purchaseSection.classList.add('quiz-results__purchase--mobile-active');
      overlay.classList.add('quiz-results__purchase-overlay--active');
      document.body.style.overflow = 'hidden'; // Prevent background scroll
    }
  }

  hideMobilePurchaseOptions() {
    const purchaseSection = this.querySelector('.quiz-results__purchase');
    const overlay = this.querySelector('.quiz-results__purchase-overlay');
    
    if (purchaseSection && overlay) {
      purchaseSection.classList.remove('quiz-results__purchase--mobile-active');
      overlay.classList.remove('quiz-results__purchase-overlay--active');
      document.body.style.overflow = ''; // Restore scroll
      
      // Reset purchase options state so next click shows options again
      this.purchaseOptionsShown = false;
      
      // Reset button text
      const completeButton = this.querySelector('.quiz__button--complete');
      if (completeButton) {
        completeButton.textContent = this.getTranslation('step9.add_to_bag');
      }
    }
  }

  handleUpdateDog() {
    const validation = this.validateDogForm();
    
    if (!validation.isValid) {
      // Show individual field errors
      this.showIndividualFieldErrors(validation);
      return;
    }
    
    const nameInput = this.querySelector('#dog-name');
    const ageGroupSelect = this.querySelector('#age-group');
    const puppyRangeSelect = this.querySelector('#puppy-range');
    const weightInput = this.querySelector('#dog-weight');
    
    const name = nameInput.value.trim();
    const ageGroup = ageGroupSelect.value;
    const puppyRange = puppyRangeSelect ? puppyRangeSelect.value : '';
    const weight = parseFloat(weightInput.value);
    
    if (!this.session.dogs) {
      this.session.dogs = [];
    }
    
    if (this.session.editing_dog_id) {
      // Update existing dog
      const dogIndex = this.session.dogs.findIndex(d => d.id === this.session.editing_dog_id);
      if (dogIndex !== -1) {
        this.session.dogs[dogIndex] = {
          id: this.session.editing_dog_id,
          name: name,
          age_group: ageGroup,
          puppy_range: puppyRange,
          weight_kg: weight
        };
      }
    } else {
      // Add new dog
      const newDog = {
        id: Date.now().toString(),
        name: name,
        age_group: ageGroup,
        puppy_range: puppyRange,
        weight_kg: weight
      };
      this.session.dogs.push(newDog);
    }
    
    // Clear editing state
    this.session.editing_dog_id = null;
    
    // Save session with updated dog data FIRST
    this.sessionManager.saveSession(this.session);
    
    // Clear all selected products when updating dog data (affects lifestage filtering)
    this.clearSelectedProducts(true, true, true);
    
    // Clear field errors and reset form
    this.clearAllFieldErrors();
    this.clearDogForm();
    
    // Update step availability based on updated dog's lifestage
    this.updateStepAvailability();
    
    // Re-render UI components
    this.populateDogProfile(this);
    this.rerenderNavigation();
    
    // Update header to reflect correct step count
    this.updateHeader();
    
    // Update pack size durations if we're on step 5
    this.refreshPackSizeDurations();
  }

  handleCancelDogEdit() {
    this.session.editing_dog_id = null;
    this.sessionManager.saveSession(this.session);
    this.populateDogProfile(this);
    this.rerenderNavigation();
  }

  handleAddDog() {
    const nameInput = this.querySelector('#dog-name');
    const ageGroupSelect = this.querySelector('#age-group');
    const puppyRangeSelect = this.querySelector('#puppy-range');
    const weightInput = this.querySelector('#dog-weight');
    
    console.log('handleAddDog called - Form values:', {
      name: nameInput?.value,
      age_group: ageGroupSelect?.value,
      puppy_range: puppyRangeSelect?.value,
      weight: weightInput?.value
    });
    
    const validation = this.validateDogForm();
    console.log('Validation result:', validation);
    
    if (!validation.isValid) {
      // Show individual field errors
      this.showIndividualFieldErrors(validation);
      return;
    }
    
    const name = nameInput.value.trim();
    const ageGroup = ageGroupSelect.value;
    const puppyRange = puppyRangeSelect ? puppyRangeSelect.value : '';
    const weight = parseFloat(weightInput.value);
    
    if (!this.session.dogs) {
      this.session.dogs = [];
    }
    
    // Add new dog to session
    const newDog = {
      id: Date.now().toString(),
      name: name,
      age_group: ageGroup,
      puppy_range: puppyRange,
      weight_kg: weight
    };
    this.session.dogs.push(newDog);
    
    // Save session with new dog FIRST
    this.sessionManager.saveSession(this.session);
    console.log('Dog added to session:', newDog, 'Total dogs:', this.session.dogs.length);
    
    // Clear all selected products when adding a new dog (affects lifestage filtering)
    this.clearSelectedProducts(true, true, true);
    
    // Clear all field errors and reset form
    this.clearAllFieldErrors();
    this.clearDogForm();
    
    // Update step availability based on new dog's lifestage
    this.updateStepAvailability();
    
    // Re-render UI components
    this.populateDogProfile(this);
    this.rerenderNavigation();
    
    // Update header to reflect correct step count
    this.updateHeader();
    
    // Update pack size durations if we're on step 5
    this.refreshPackSizeDurations();
  }

  handleEditDog(dogId) {
    this.session.editing_dog_id = dogId;
    this.sessionManager.saveSession(this.session);
    this.populateDogProfile(this);
    this.rerenderNavigation();
  }

  handleDeleteDog(dogId) {
    if (!this.session.dogs) return;
    
    this.session.dogs = this.session.dogs.filter(dog => dog.id !== dogId);
    this.session.editing_dog_id = null;
    
    // Save session with deleted dog first
    this.sessionManager.saveSession(this.session);
    
    // Clear all selected products when deleting a dog (affects lifestage filtering)
    this.clearSelectedProducts(true, true, true);
    
    // Update step availability based on remaining dogs' lifestages
    this.updateStepAvailability();
    
    // Re-render UI components
    this.populateDogProfile(this);
    this.rerenderNavigation();
    
    // Update header to reflect correct step count
    this.updateHeader();
  }

  handleMoreInfoToggle(button) {
    const feedingOption = button.closest('.quiz__feeding-style-option');
    if (!feedingOption) return;
    
    const moreInfoDiv = feedingOption.querySelector('.quiz__feeding-style-more-info');
    if (!moreInfoDiv) return;
    
    const isVisible = moreInfoDiv.style.display !== 'none';
    
    if (isVisible) {
      moreInfoDiv.style.display = 'none';
      document.body.classList.remove('quiz__modal-open');
    } else {
      this.closeAllMoreInfoModals();
      moreInfoDiv.style.display = 'block';
      document.body.classList.add('quiz__modal-open');
    }
  }

  handleMoreInfoClose() {
    this.closeAllMoreInfoModals();
    document.body.classList.remove('quiz__modal-open');
  }

  closeAllMoreInfoModals() {
    const allModals = this.querySelectorAll('.quiz__feeding-style-more-info');
    allModals.forEach(modal => {
      modal.style.display = 'none';
    });
  }

  handleCloseQuiz() {
    const modal = document.getElementById('QuizModal');
    if (modal && typeof modal.hide === 'function') {
      // this.sessionManager.clearSession();
      modal.hide();
      return;
    }
    // this.sessionManager.clearSession();
    window.location.href = '/';
  }

  showValidationMessage(message) {
    const errorContainer = this.querySelector('#quiz-validation-errors');
    if (errorContainer) {
      const errorText = errorContainer.querySelector('.quiz__error-text');
      if (errorText) {
        errorText.textContent = message;
        errorContainer.style.display = 'block';
      }
    }
  }

  handleFieldBlur(event) {
    const field = event.target;
    if (field.dataset.touched === 'true') {
      this.validateAndShowFieldError(field);
    }
  }

  validateAndShowFieldError(field) {
    const fieldName = field.name || field.id;
    const fieldValue = field.value;
    
    let validation = { isValid: true, errors: [] };
    
    switch (fieldName) {
      case 'dog-name':
        validation = this.validator.validateDogName(fieldValue);
        break;
      case 'age-group':
        validation = this.validator.validateAgeGroup(fieldValue);
        break;
      case 'puppy-range':
        validation = this.validator.validatePuppyRange(fieldValue);
        break;
      case 'dog-weight':
        validation = this.validator.validateDogWeight(fieldValue);
        break;
    }
    
    if (validation.isValid) {
      this.clearFieldError(fieldName);
    } else {
      this.showFieldError(fieldName, validation.errors[0]);
    }
  }

  showFieldValidationErrors(validation) {
    // Only show errors for fields that have been touched
    const nameInput = this.querySelector('#dog-name');
    const ageGroupSelect = this.querySelector('#age-group');
    const puppyRangeSelect = this.querySelector('#puppy-range');
    const weightInput = this.querySelector('#dog-weight');
    
    if (nameInput && nameInput.dataset.touched === 'true') {
      const nameValidation = this.validator.validateDogName(nameInput.value);
      if (nameValidation.isValid) {
        this.clearFieldError('dog-name');
      } else {
        this.showFieldError('dog-name', nameValidation.errors[0]);
      }
    }
    
    if (ageGroupSelect && ageGroupSelect.dataset.touched === 'true') {
      const ageGroupValidation = this.validator.validateAgeGroup(ageGroupSelect.value);
      if (ageGroupValidation.isValid) {
        this.clearFieldError('age-group');
      } else {
        this.showFieldError('age-group', ageGroupValidation.errors[0]);
      }
    }
    
    if (puppyRangeSelect && puppyRangeSelect.dataset.touched === 'true') {
      const puppyRangeValidation = this.validator.validatePuppyRange(puppyRangeSelect.value);
      if (puppyRangeValidation.isValid) {
        this.clearFieldError('puppy-range');
      } else {
        this.showFieldError('puppy-range', puppyRangeValidation.errors[0]);
      }
    }
    
    if (weightInput && weightInput.dataset.touched === 'true') {
      const weightValidation = this.validator.validateDogWeight(weightInput.value);
      if (weightValidation.isValid) {
        this.clearFieldError('dog-weight');
      } else {
        this.showFieldError('dog-weight', weightValidation.errors[0]);
      }
    }
  }

  showFieldError(fieldName, message) {
    const errorElement = this.querySelector(`#${fieldName}-error`);
    const inputElement = this.querySelector(`#${fieldName}`);
    
    if (errorElement) {
      errorElement.textContent = message;
      errorElement.style.display = 'block';
    }
    
    if (inputElement) {
      inputElement.classList.remove('quiz__input--valid');
      inputElement.classList.add('quiz__input--error');
    }
  }

  clearFieldError(fieldName) {
    const errorElement = this.querySelector(`#${fieldName}-error`);
    const inputElement = this.querySelector(`#${fieldName}`);
    
    if (errorElement) {
      errorElement.style.display = 'none';
      errorElement.textContent = '';
    }
    
    if (inputElement) {
      inputElement.classList.remove('quiz__input--error');
      inputElement.classList.add('quiz__input--valid');
    }
  }

  clearAllFieldErrors() {
    ['dog-name', 'age-group', 'puppy-range', 'dog-weight'].forEach(fieldName => {
      this.clearFieldError(fieldName);
      const inputElement = this.querySelector(`#${fieldName}`);
      if (inputElement) {
        inputElement.classList.remove('quiz__input--error', 'quiz__input--valid');
      }
    });
  }

  showIndividualFieldErrors(validation) {
    // Show specific field errors based on validation result
    if (validation.errors && validation.errors.name) {
      this.showFieldError('dog-name', validation.errors.name[0]);
    }
    if (validation.errors && validation.errors.age_group) {
      this.showFieldError('age-group', validation.errors.age_group[0]);
    }
    if (validation.errors && validation.errors.puppy_range) {
      this.showFieldError('puppy-range', validation.errors.puppy_range[0]);
    }
    if (validation.errors && validation.errors.weight) {
      this.showFieldError('dog-weight', validation.errors.weight[0]);
    }
  }

  clearDogForm() {
    const nameInput = this.querySelector('#dog-name');
    const ageGroupSelect = this.querySelector('#age-group');
    const puppyRangeSelect = this.querySelector('#puppy-range');
    const puppyRangeContainer = this.querySelector('.puppy-range-container');
    const weightInput = this.querySelector('#dog-weight');
    
    if (nameInput) {
      nameInput.value = '';
      nameInput.dataset.touched = 'false';
      nameInput.classList.remove('quiz__input--error', 'quiz__input--valid');
    }
    if (ageGroupSelect) {
      ageGroupSelect.value = '';
      ageGroupSelect.dataset.touched = 'false';
      ageGroupSelect.classList.remove('quiz__input--error', 'quiz__input--valid');
    }
    if (puppyRangeSelect) {
      puppyRangeSelect.value = '';
      puppyRangeSelect.required = false;
      puppyRangeSelect.dataset.touched = 'false';
      puppyRangeSelect.classList.remove('quiz__input--error', 'quiz__input--valid');
    }
    if (puppyRangeContainer) {
      puppyRangeContainer.style.display = 'none';
    }
    if (weightInput) {
      weightInput.value = '';
      weightInput.dataset.touched = 'false';
      weightInput.classList.remove('quiz__input--error', 'quiz__input--valid');
    }
  }

  renderTreatsSupplementsProducts(container, products, type) {
    const template = document.getElementById('quiz-treats-supplements-card-template');
    if (!template) return;
    
    container.innerHTML = '';
    
    products.forEach(product => {
      const productCard = template.content.cloneNode(true);
       
      // Set product data
      const cardElement = productCard.querySelector('.quiz-treats-supplements__card');
      cardElement.setAttribute('data-product-id', product.id);
      cardElement.setAttribute('data-product-type', type);
      
      // Set card background color from metafield
      const cardBackground = product.metafields?.custom?.card_background || '#fbf1e3';
      console.log(cardBackground);
      cardElement.style.setProperty('--quiz-card-background', cardBackground);
      
      // Set product image
      const imageContainer = productCard.querySelector('.quiz-treats-supplements__card-image');
      if (product.featured_image) {
        const imageUrl = product.url || '#';
        imageContainer.innerHTML = `<a href="${imageUrl}" class="quiz-treats-supplements__image-link"><img src="${product.featured_image.url + '&width=600'}" alt="${product.featured_image.alt || product.title}" loading="lazy" /></a>`;
      }
      
      // Set product info
      const title = productCard.querySelector('.quiz-treats-supplements__card-title');
      const titleLink = productCard.querySelector('.quiz-treats-supplements__title-link');
      const description = productCard.querySelector('.quiz-treats-supplements__card-description');
      const price = productCard.querySelector('.quiz-treats-supplements__card-price');
      const quantityDisplay = productCard.querySelector('.quiz-treats-supplements__quantity-display');
      
      if (title) title.textContent = product.title;
      if (description) {
        const desc = product.description || '';
        description.textContent = desc;
      }

      if (price) {
        price.textContent = this.formatPrice(product.price);
      }

      if (titleLink && product.url) {
        titleLink.href = product.url;
      }
      
      // Get current quantity from session data
      const sessionKey = type === 'treats' ? 'selected_treats' : 'selected_supplements';
      const sessionItem = this.session[sessionKey]?.find(item => item.id === product.id);
      const currentQuantity = sessionItem ? sessionItem.quantity : 0;
      
      if (quantityDisplay) {
        // Handle both input and span/div elements
        if (quantityDisplay.tagName === 'INPUT') {
          quantityDisplay.value = currentQuantity.toString();
        } else {
          quantityDisplay.textContent = currentQuantity.toString();
        }
      }
      
      // Set up quantity buttons
      const minusBtn = productCard.querySelector('.quiz-treats-supplements__quantity-btn--minus');
      const plusBtn = productCard.querySelector('.quiz-treats-supplements__quantity-btn--plus');
      
      if (minusBtn) {
        minusBtn.addEventListener('click', () => this.updateTreatsSupplementsQuantity(product.id, -1, type));
      }
      
      if (plusBtn) {
        plusBtn.addEventListener('click', () => this.updateTreatsSupplementsQuantity(product.id, 1, type));
      }
      
      container.appendChild(productCard);
    });
  }

  updateTreatsSupplementsQuantity(productId, change, type) {
    const sessionKey = type === 'treats' ? 'selected_treats' : 'selected_supplements';
    
    if (!this.session[sessionKey]) {
      this.session[sessionKey] = [];
    }
    
    const existingItem = this.session[sessionKey].find(item => item.id === productId);
    
    if (existingItem) {
      existingItem.quantity = Math.max(0, existingItem.quantity + change);
      
      if (existingItem.quantity === 0) {
        this.session[sessionKey] = this.session[sessionKey].filter(item => item.id !== productId);
      }
    } else if (change > 0) {
      // Find product data
      const productData = window.QuizProductData?.[type === 'treats' ? 'Treats' : 'Supplements']?.find(p => p.id === productId);
      if (productData) {
        this.session[sessionKey].push({
          id: productId,
          variant_id: productData.variant_id,
          title: productData.title,
          quantity: 1,
          description: productData.description,
          featured_image: productData.featured_image
        });
      }
    }
    
    // Update session
    this.session = this.sessionManager.updateSession(this.session);
    
    // Update UI
    this.updateProductQuantityDisplay(productId, type);
    
    // Update status message in navigation for steps 7-8
    if (this.session.current_step === 7 || this.session.current_step === 8) {
      this.rerenderNavigation();
    }
    
    console.log(`Updated ${type} selection:`, this.session[sessionKey]);
  }

  updateProductQuantityDisplay(productId, type) {
    const card = document.querySelector(`[data-product-id="${productId}"][data-product-type="${type}"]`);
    if (!card) return;
    
    const quantityDisplay = card.querySelector('.quiz-treats-supplements__quantity-display');
    if (!quantityDisplay) return;
    
    const sessionKey = type === 'treats' ? 'selected_treats' : 'selected_supplements';
    const item = this.session[sessionKey]?.find(item => item.id === productId);
    const quantity = item ? item.quantity : 0;
    
    // Handle both input and span/div elements
    if (quantityDisplay.tagName === 'INPUT') {
      quantityDisplay.value = quantity.toString();
    } else {
      quantityDisplay.textContent = quantity.toString();
    }
    
    // Add animation
    quantityDisplay.classList.add('quiz-treats-supplements__quantity-display--updating');
    setTimeout(() => {
      quantityDisplay.classList.remove('quiz-treats-supplements__quantity-display--updating');
    }, 200);
  }



  getCurrentStepElement() {
    return this.querySelector('.quiz__step');
  }

  getSession() {
    return this.sessionManager.getSession();
  }

  navigateToStep(stepNumber) {
    this.session.current_step = stepNumber;
    this.session = this.sessionManager.updateSession({ current_step: stepNumber });
    this.render();
    this.updateHeader();
  }

  groupProductsByCollections(products) {
    const groups = {};
    const groupOrder = [];

    products.forEach(product => {
      const groupKey = this.getQuizGroupKey(product) || this.getCollectionKey(product.collections);
      if (!groups[groupKey]) {
        groups[groupKey] = [];
        groupOrder.push(groupKey);
      }
      groups[groupKey].push(product);
    });

    return { groups, groupOrder };
  }

  getQuizGroupKey(product) {
    const raw = product?.quiz_group;
    if (!raw || (typeof raw === 'string' && raw.trim() === '')) return '';
    if (Array.isArray(raw)) return raw.filter(Boolean).join('|');
    return String(raw).trim();
  }

  getCollectionKey(collections) {
    if (!collections || collections.length === 0) {
      return 'Other';
    }

    
    
    // Sort collections by id to ensure consistent grouping
    const sortedCollections = [...collections].sort((a, b) => a.id - b.id);
    
    // Create a key from collection ids
    return sortedCollections[0].title
  }

  // Step 9: Results Page Methods
  populateResultsPage(stepElement) {
    this.populateOrderSummary(stepElement);
    this.setupExpandableToggles(stepElement);
    this.setupPurchaseOptions(stepElement);
    
    // Set up delivery frequency dropdown BEFORE calculating pricing
    this.setupDeliveryFrequencyHandler(stepElement);
    
    // Now calculate pricing with properly initialized dropdown
    this.calculatePricing(stepElement);
    
    this.setupAddToCartHandlers(stepElement);
  }

  populateOrderSummary(stepElement) {
    const session = this.getSession();
    
    // Populate dogs count
    const dogsCount = stepElement.querySelector('#dogs-count');
    if (dogsCount) {
      const dogCount = session.dogs ? session.dogs.length : 1;
      dogsCount.textContent = `${dogCount} dog${dogCount > 1 ? 's' : ''}`;
    }
    
    // Populate box size
    const boxSizeMeta = stepElement.querySelector('#box-size-meta');
    if (boxSizeMeta && session.selected_pack_size) {
      const packSize = session.selected_pack_size.replace(' MEALS', '');
      const duration = this.calculateDuration(parseInt(packSize), session.dogs);
      boxSizeMeta.textContent = `${session.selected_pack_size} - will last ${duration} days`;
    }
    
    // Populate recipes count
    const recipesCount = stepElement.querySelector('#recipes-count');
    if (recipesCount && session.selected_recipes) {
      const recipeCount = session.selected_recipes.length;
      recipesCount.textContent = `${recipeCount} varieties selected`;
    }
    
    // Populate extras count
    const extrasCount = stepElement.querySelector('#extras-count');
    if (extrasCount) {
      const treatsCount = session.selected_treats ? session.selected_treats.length : 0;
      const supplementsCount = session.selected_supplements ? session.selected_supplements.length : 0;
      const totalExtras = treatsCount + supplementsCount;
      extrasCount.textContent = `${totalExtras} extras selected`;
    }
  }

  setupExpandableToggles(stepElement) {
    const headers = stepElement.querySelectorAll('.quiz-results__summary-header');
    
    headers.forEach(header => {
      header.addEventListener('click', () => {
        const item = header.closest('.quiz-results__summary-item');
        const content = item.querySelector('.quiz-results__summary-content');
        const toggle = header.querySelector('.quiz-results__summary-toggle');
        const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
        
        if (isExpanded) {
          this.collapseSection(toggle, content);
        } else {
          this.expandSection(toggle, content, item);
        }
      });
    });
  }

  expandSection(toggle, content, item) {
    const section = item.dataset.section;
    
    // Set expanded state
    toggle.setAttribute('aria-expanded', 'true');
    content.classList.add('quiz-results__summary-content--expanded');
    
    // Populate section content
    switch (section) {
      case 'dogs':
        this.populateDogsDetails(content);
        break;
      case 'box-size':
        this.populateBoxSizeDetails(content);
        break;
      case 'recipes':
        this.populateRecipesDetails(content);
        break;
      case 'extras':
        this.populateExtrasDetails(content);
        break;
    }
  }

  collapseSection(toggle, content) {
    toggle.setAttribute('aria-expanded', 'false');
    content.classList.remove('quiz-results__summary-content--expanded');
  }

  populateDogsDetails(content) {
    const session = this.getSession();
    
    if (!session.dogs || session.dogs.length === 0) {
      content.innerHTML = '<p class="quiz-results__error">No dog information available</p>';
      return;
    }
    
    const dogHtml = session.dogs.map(dog => `
      <div class="quiz-results__dog-details">
        <div class="quiz-results__dog-info">
          <h5 class="quiz-results__dog-name">${dog.name || 'Unknown'}</h5>
          <p class="quiz-results__dog-meta">Age Group: ${dog.age_group || 'N/A'}</p>
          ${dog.age_group === 'puppy' && dog.puppy_range ? `<p class="quiz-results__dog-meta">Puppy Range: ${dog.puppy_range}</p>` : ''}
          <p class="quiz-results__dog-meta">Weight: ${dog.weight_kg || dog.weight || 'N/A'} kg</p>
        </div>
      </div>
    `).join('');
    
    content.innerHTML = dogHtml;

    content.innerHTML += `<button type="button" class="quiz-results__edit-button" data-edit-section="dogs">
          <span class="quiz-results__edit-text">Edit</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
          <path d="M5 19.9224H6.425L16.2 10.1474L14.775 8.72236L5 18.4974V19.9224ZM3 21.9224V17.6724L16.2 4.49736C16.4 4.31403 16.6208 4.17236 16.8625 4.07236C17.1042 3.97236 17.3583 3.92236 17.625 3.92236C17.8917 3.92236 18.15 3.97236 18.4 4.07236C18.65 4.17236 18.8667 4.32236 19.05 4.52236L20.425 5.92236C20.625 6.1057 20.7708 6.32236 20.8625 6.57236C20.9542 6.82236 21 7.07236 21 7.32236C21 7.58903 20.9542 7.8432 20.8625 8.08486C20.7708 8.32653 20.625 8.54736 20.425 8.74736L7.25 21.9224H3ZM15.475 9.44736L14.775 8.72236L16.2 10.1474L15.475 9.44736Z" fill="#18303F"/>
        </svg>
        </button>`;
    
    // Add edit handlers
    const editButtons = content.querySelectorAll('.quiz-results__edit-button');
    editButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.navigateToStep(1);
      });
    });
  }

  populateBoxSizeDetails(content) {
    const session = this.getSession();
    
    if (!session.selected_pack_size || !session.feeding_style) {
      content.innerHTML = '<p class="quiz-results__error">No box size information available</p>';
      return;
    }
    
    const packSize = session.selected_pack_size.replace(' MEALS', '');
    const duration = this.calculateDuration(parseInt(packSize), session.dogs);
    const feedingStyle = session.feeding_style;
    const dietaryRestrictions = session.avoided_ingredients && session.avoided_ingredients.length > 0 
      ? session.avoided_ingredients.join(', ') 
      : 'None';
    
    content.innerHTML = `
      <div class="quiz-results__box-details">
        <div class="quiz-results__box-info">
          <h5 class="quiz-results__box-title">${session.selected_pack_size}</h5>
          <p class="quiz-results__box-meta">Duration: ${duration} days</p>
          <p class="quiz-results__box-meta">Feeding style: ${feedingStyle}</p>
          <p class="quiz-results__box-meta">Dietary considerations: ${dietaryRestrictions}</p>
        </div>
        <button type="button" class="quiz-results__edit-button" data-edit-section="box-size">
          <span class="quiz-results__edit-text">Edit</span>
           <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
          <path d="M5 19.9224H6.425L16.2 10.1474L14.775 8.72236L5 18.4974V19.9224ZM3 21.9224V17.6724L16.2 4.49736C16.4 4.31403 16.6208 4.17236 16.8625 4.07236C17.1042 3.97236 17.3583 3.92236 17.625 3.92236C17.8917 3.92236 18.15 3.97236 18.4 4.07236C18.65 4.17236 18.8667 4.32236 19.05 4.52236L20.425 5.92236C20.625 6.1057 20.7708 6.32236 20.8625 6.57236C20.9542 6.82236 21 7.07236 21 7.32236C21 7.58903 20.9542 7.8432 20.8625 8.08486C20.7708 8.32653 20.625 8.54736 20.425 8.74736L7.25 21.9224H3ZM15.475 9.44736L14.775 8.72236L16.2 10.1474L15.475 9.44736Z" fill="#18303F"/>
        </svg>
        </button>
      </div>
    `;
    
    // Add edit handler
    const editButton = content.querySelector('.quiz-results__edit-button');
    editButton.addEventListener('click', () => {
      this.navigateToStep(5);
    });
  }

  populateRecipesDetails(content) {
    const session = this.getSession();
    console.log(session.selected_recipes);
    
    if (!session.selected_recipes || session.selected_recipes.length === 0) {
      content.innerHTML = `
      <p class="quiz-results__error">No recipes selected</p>
      <button type="button" class="quiz-results__edit-button" data-edit-section="recipes">
      <span class="quiz-results__edit-text">Edit</span>
       <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
      <path d="M5 19.9224H6.425L16.2 10.1474L14.775 8.72236L5 18.4974V19.9224ZM3 21.9224V17.6724L16.2 4.49736C16.4 4.31403 16.6208 4.17236 16.8625 4.07236C17.1042 3.97236 17.3583 3.92236 17.625 3.92236C17.8917 3.92236 18.15 3.97236 18.4 4.07236C18.65 4.17236 18.8667 4.32236 19.05 4.52236L20.425 5.92236C20.625 6.1057 20.7708 6.32236 20.8625 6.57236C20.9542 6.82236 21 7.07236 21 7.32236C21 7.58903 20.9542 7.8432 20.8625 8.08486C20.7708 8.32653 20.625 8.54736 20.425 8.74736L7.25 21.9224H3ZM15.475 9.44736L14.775 8.72236L16.2 10.1474L15.475 9.44736Z" fill="#18303F"/>
    </svg>
    </button>
    `;
    const editButton = content.querySelector('.quiz-results__edit-button');
    editButton.addEventListener('click', () => {
      this.navigateToStep(6);
    });
      return;
    }
    
    const recipesHtml = session.selected_recipes.map(recipe => `
      <div class="quiz-results__product-card">
        <div class="quiz-results__product-image">
          ${recipe.featured_image ? `<img src="${recipe.featured_image.url + '&width=200'}" alt="${recipe.featured_image.alt}">` : '<div class="quiz-results__product-placeholder">No image</div>'}
        </div> 
        <div class="quiz-results__product-content">
          <div class="quiz-results__product-info">
            <h6 class="quiz-results__product-title">${recipe.title}</h6>
            <p class="quiz-results__product-description">${this.truncateText(recipe.description || '', 80)}</p>
          </div>
          <div class="quiz-results__product-quantity"> ${recipe.quantity} pack${recipe.quantity > 1 ? 's' : ''}</div>
        </div>
      </div>
    `).join('');
    
    content.innerHTML = `
      <div class="quiz-results__product-cards">
        ${recipesHtml}
      </div>
      <button type="button" class="quiz-results__edit-button" data-edit-section="recipes">
          <span class="quiz-results__edit-text">Edit</span>
           <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
          <path d="M5 19.9224H6.425L16.2 10.1474L14.775 8.72236L5 18.4974V19.9224ZM3 21.9224V17.6724L16.2 4.49736C16.4 4.31403 16.6208 4.17236 16.8625 4.07236C17.1042 3.97236 17.3583 3.92236 17.625 3.92236C17.8917 3.92236 18.15 3.97236 18.4 4.07236C18.65 4.17236 18.8667 4.32236 19.05 4.52236L20.425 5.92236C20.625 6.1057 20.7708 6.32236 20.8625 6.57236C20.9542 6.82236 21 7.07236 21 7.32236C21 7.58903 20.9542 7.8432 20.8625 8.08486C20.7708 8.32653 20.625 8.54736 20.425 8.74736L7.25 21.9224H3ZM15.475 9.44736L14.775 8.72236L16.2 10.1474L15.475 9.44736Z" fill="#18303F"/>
        </svg>
        </button>
    `;
    
    // Add edit handler
    const editButton = content.querySelector('.quiz-results__edit-button');
    editButton.addEventListener('click', () => {
      this.navigateToStep(6);
    });
  }

  populateExtrasDetails(content) {
    const session = this.getSession();
    const treats = session.selected_treats || [];
    const supplements = session.selected_supplements || [];
    const allExtras = [...treats, ...supplements];
    
    if (allExtras.length === 0) {
      content.innerHTML = `
      <p class="quiz-results__error">No extras selected</p>
      <button type="button" class="quiz-results__edit-button" data-edit-section="extras">
          <span class="quiz-results__edit-text">Edit</span>
           <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
          <path d="M5 19.9224H6.425L16.2 10.1474L14.775 8.72236L5 18.4974V19.9224ZM3 21.9224V17.6724L16.2 4.49736C16.4 4.31403 16.6208 4.17236 16.8625 4.07236C17.1042 3.97236 17.3583 3.92236 17.625 3.92236C17.8917 3.92236 18.15 3.97236 18.4 4.07236C18.65 4.17236 18.8667 4.32236 19.05 4.52236L20.425 5.92236C20.625 6.1057 20.7708 6.32236 20.8625 6.57236C20.9542 6.82236 21 7.07236 21 7.32236C21 7.58903 20.9542 7.8432 20.8625 8.08486C20.7708 8.32653 20.625 8.54736 20.425 8.74736L7.25 21.9224H3ZM15.475 9.44736L14.775 8.72236L16.2 10.1474L15.475 9.44736Z" fill="#18303F"/>
        </svg>
        </button>
      `;
      const editButton = content.querySelector('.quiz-results__edit-button');
      editButton.addEventListener('click', () => {
        this.navigateToStep(7);
      }); 
      return;
    }
    
    const extrasHtml = allExtras.map(extra => `
      <div class="quiz-results__product-card">
        <div class="quiz-results__product-image">
          ${extra.featured_image ? `<img src="${extra.featured_image.url + '&width=200'}" alt="${extra.featured_image.alt}">` : '<div class="quiz-results__product-placeholder">No image</div>'}
        </div>
        <div class="quiz-results__product-content">
          <div class="quiz-results__product-info">
            <h6 class="quiz-results__product-title">${extra.title}</h6>
            <p class="quiz-results__product-description">${this.truncateText(extra.description || '', 80)}</p>
          </div>
          <div class="quiz-results__product-quantity"> ${extra.quantity} item${extra.quantity > 1 ? 's' : ''}</div>
        </div>
      </div> 
    `).join('');
     
    content.innerHTML = `
      <div class="quiz-results__product-cards">
        ${extrasHtml}
      </div>
      <button type="button" class="quiz-results__edit-button" data-edit-section="extras">
          <span class="quiz-results__edit-text">Edit</span>
           <svg xmlns="http://www.w3.org/2000/svg" width="24" height="25" viewBox="0 0 24 25" fill="none">
          <path d="M5 19.9224H6.425L16.2 10.1474L14.775 8.72236L5 18.4974V19.9224ZM3 21.9224V17.6724L16.2 4.49736C16.4 4.31403 16.6208 4.17236 16.8625 4.07236C17.1042 3.97236 17.3583 3.92236 17.625 3.92236C17.8917 3.92236 18.15 3.97236 18.4 4.07236C18.65 4.17236 18.8667 4.32236 19.05 4.52236L20.425 5.92236C20.625 6.1057 20.7708 6.32236 20.8625 6.57236C20.9542 6.82236 21 7.07236 21 7.32236C21 7.58903 20.9542 7.8432 20.8625 8.08486C20.7708 8.32653 20.625 8.54736 20.425 8.74736L7.25 21.9224H3ZM15.475 9.44736L14.775 8.72236L16.2 10.1474L15.475 9.44736Z" fill="#18303F"/>
        </svg>
        </button>
    `;
    
    // Add edit handler
    const editButton = content.querySelector('.quiz-results__edit-button');
    editButton.addEventListener('click', () => {
      this.navigateToStep(7);
    });
  }

  setupPurchaseOptions(stepElement) {
    const radioButtons = stepElement.querySelectorAll('input[name="purchase-option"]');
    
    radioButtons.forEach(radio => {
      radio.addEventListener('change', () => {
        this.updatePurchaseSelection(stepElement, radio.value);
      });
    });
  }

  updatePurchaseSelection(stepElement, selectedOption) {
    const subscriptionOption = stepElement.querySelector('.quiz-results__purchase-option--subscription');
    const onetimeOption = stepElement.querySelector('.quiz-results__purchase-option--onetime');
    
    if (selectedOption === 'subscription') {
      onetimeOption.classList.remove('quiz-results__purchase-option--selected');
      subscriptionOption.classList.add('quiz-results__purchase-option--selected');
    } else {
      subscriptionOption.classList.remove('quiz-results__purchase-option--selected');
      onetimeOption.classList.add('quiz-results__purchase-option--selected');
    }
  }

  calculatePricing(stepElement) {
    const session = this.getSession();
    
    // Get the selected selling plan ID from the dropdown or config
    const deliveryFrequencySelect = stepElement.querySelector('#delivery-frequency');
    const selectedSellingPlanId = deliveryFrequencySelect ? deliveryFrequencySelect.value : this.config.selected_selling_plan?.id;
    
    console.log('Calculating pricing with selling plan ID:', selectedSellingPlanId);
    
    // Calculate pricing with the specific selling plan
    const pricing = this.dynamicPricing.updatePricing(session, selectedSellingPlanId);
    
    // Update subscription pricing
    const subscriptionPriceElement = stepElement.querySelector('#subscription-price');
    const subscriptionSavingsElement = stepElement.querySelector('#subscription-savings');
    const subscriptionDiscountElement = stepElement.querySelector('.quiz-results__purchase-discount');
    
    if (subscriptionPriceElement) {
      subscriptionPriceElement.textContent = pricing.formatted.subscriptionPrice;
    }
    if (subscriptionSavingsElement) {
      subscriptionSavingsElement.textContent = pricing.formatted.subscriptionSavings;
    }
    if (subscriptionDiscountElement) {
      subscriptionDiscountElement.textContent = pricing.formatted.subscriptionDiscount;
    }
    
    // Update one-time pricing
    const onetimePriceElement = stepElement.querySelector('#onetime-price');
    if (onetimePriceElement) {
      onetimePriceElement.textContent = pricing.formatted.basePrice;
    }
  }

  setupDeliveryFrequencyHandler(stepElement) {
    const deliveryFrequencySelect = stepElement.querySelector('#delivery-frequency');
    
    if (deliveryFrequencySelect) {
      // Initialize with the selected selling plan from config
      if (this.config.selected_selling_plan && this.config.selected_selling_plan.id) {
        const selectedPlanId = this.config.selected_selling_plan.id.toString();
        console.log('Initializing delivery frequency with selected selling plan:', selectedPlanId);
        
        // Set the dropdown to the correct selling plan
        deliveryFrequencySelect.value = selectedPlanId;
        
        // Double-check the value was set correctly
        if (deliveryFrequencySelect.value !== selectedPlanId) {
          console.warn('Could not set delivery frequency to:', selectedPlanId);
          console.log('Available options:', Array.from(deliveryFrequencySelect.options).map(opt => ({value: opt.value, text: opt.text})));
        } else {
          console.log('Delivery frequency successfully set to:', deliveryFrequencySelect.value, deliveryFrequencySelect.options[deliveryFrequencySelect.selectedIndex].text);
        }
      }
      
      // Set up change handler
      deliveryFrequencySelect.addEventListener('change', (e) => {
        const selectedPlanId = e.target.value;
        this.handleDeliveryFrequencyChange(selectedPlanId, stepElement);
      });
    }
  }

  handleDeliveryFrequencyChange(sellingPlanId, stepElement) {
    // Update the selected selling plan
    this.dynamicPricing.updateSelectedSellingPlan(sellingPlanId);
    
    // Recalculate pricing with new selling plan
    const session = this.getSession();
    const pricing = this.dynamicPricing.updatePricing(session, sellingPlanId);
    
    // Update subscription pricing elements
    const subscriptionPriceElement = stepElement.querySelector('#subscription-price');
    const subscriptionSavingsElement = stepElement.querySelector('#subscription-savings');
    const subscriptionDiscountElement = stepElement.querySelector('.quiz-results__purchase-discount');
    
    if (subscriptionPriceElement) {
      subscriptionPriceElement.textContent = pricing.formatted.subscriptionPrice;
      this.animateElement(subscriptionPriceElement);
    }
    if (subscriptionSavingsElement) {
      subscriptionSavingsElement.textContent = pricing.formatted.subscriptionSavings;
      this.animateElement(subscriptionSavingsElement);
    }
    if (subscriptionDiscountElement) {
      subscriptionDiscountElement.textContent = pricing.formatted.subscriptionDiscount;
      this.animateElement(subscriptionDiscountElement);
    }
    
    // Update session with selected selling plan
    this.session.selected_selling_plan_id = sellingPlanId;
    this.sessionManager.saveSession(this.session);
    
    console.log('Delivery frequency changed:', sellingPlanId);
    console.log('Updated pricing:', pricing);
  }

  animateElement(element) {
    element.style.transform = 'scale(1.05)';
    element.style.transition = 'transform 0.2s ease';
    
    setTimeout(() => {
      element.style.transform = 'scale(1)';
      setTimeout(() => {
        element.style.transition = '';
      }, 200);
    }, 200);
  }

  calculateDuration(packSize, dogs) {
    if (!dogs || dogs.length === 0) return 21;
    
    const totalDailyPortions = dogs.reduce((sum, dog) => {
      const portions = this.calculateDailyPortionsForDog(dog);
      return sum + portions;
    }, 0);
    
    const exactDuration = packSize / totalDailyPortions;
    const duration = Math.max(1, Math.floor(exactDuration));
    
    return duration;
  }

  calculateDailyPortionsForDog(dog) {
    if (!dog.weight_kg || !dog.age_group) return 1;
    
    const dailyGramsNeeded = this.calculateDailyGramsForDog(dog);
    const packWeightGrams = this.getPackWeightGrams();
    const dailyPortions = dailyGramsNeeded / packWeightGrams;
    const result = Math.max(0.01, Math.round(dailyPortions * 100) / 100);
    
    return result;
  }





  getPackWeightGrams() {
    // Get pack weight from recipe data or use 500g default
    let packWeightGrams = 500;
    
    if (window.QuizProductData?.Recipe?.length > 0) {
      const recipes = window.QuizProductData.Recipe;
      for (const recipe of recipes) {
        const variant = recipe.first_available_variant || {};
        const unit = (variant.weight_unit || '').toLowerCase();
        const weightInUnit = variant.weight_in_unit;
        
        if (typeof weightInUnit === 'number' && weightInUnit > 0) {
          if (unit === 'kg') {
            packWeightGrams = Math.round(weightInUnit * 1000);
          } else if (unit === 'g') {
            packWeightGrams = Math.round(weightInUnit);
          }
          // Accept only reasonable weights (200g - 2kg)
          if (packWeightGrams >= 200 && packWeightGrams <= 2000) {
            break;
          }
        }
        
        // Fallback to recipe.weight if available
        if (typeof recipe.weight === 'number' && recipe.weight > 0) {
          packWeightGrams = Math.round(recipe.weight);
          if (packWeightGrams >= 200 && packWeightGrams <= 2000) {
            break;
          }
        }
      }
    }
    
    // Ensure reasonable pack weight
    if (packWeightGrams < 200 || packWeightGrams > 2000) {
      packWeightGrams = 500;
    }
    
    return packWeightGrams;
  }

  setupAddToCartHandlers(stepElement) {
    const subscriptionButton = stepElement.querySelector('#subscription-add-to-bag');
    const onetimeButton = stepElement.querySelector('#onetime-add-to-bag');
    
    if (subscriptionButton) {
      subscriptionButton.addEventListener('click', () => {
        this.addToCart('subscription', stepElement);
      });
    }
    
    if (onetimeButton) {
      onetimeButton.addEventListener('click', () => {
        this.addToCart('onetime', stepElement);
      });
    }
  }

  async addToCart(purchaseType, stepElement) {
    const session = this.getSession();
    const button = stepElement.querySelector(`#${purchaseType}-add-to-bag`);
    
    if (!button) return;
    
    // Disable button and show loading
    button.disabled = true;
    button.textContent = this.getTranslation('step9.adding');
    
    try {
      // Create customer with quiz data first
      await this.createCustomerWithQuizData(session);
      
      if (purchaseType === 'subscription') {
        await this.createRechargeBundle(session, stepElement);
      } else {
        await this.addToCartOneTime(session);
      }
      
      // Success - handle post-add actions
      this.showSuccessMessage();
      if (window.vennApps?.isAppView) {
        // In app: let native handle cart/checkout, don't redirect
        return;
      }
      window.location.href = '/checkout';
      // If we're on the cart page, close the quiz instead of opening ajax cart
      if (window.location.pathname === '/cart') {
        setTimeout(async () => {
          // Update all cart-related content
          await this.updateAllCartContent();
          this.handleCloseQuiz();
        }, 1000); // Give time to show success message
      } else {
        await this.#openAjaxCart();
      }
      
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showError('Failed to add to cart. Please try again.');
    } finally {
      // Re-enable button
      button.disabled = false;
      button.textContent = this.getTranslation('step9.add_to_bag');
    }
  }

  async createRechargeBundle(session, stepElement) {
    const deliveryFrequency = stepElement.querySelector('#delivery-frequency').value;
    
    try {
      const cartItems = await this.dynamicPricing.createRechargeBundle(session, deliveryFrequency);
      console.log('Cart items to add:', cartItems);
      
      if (window.VennAppsCartBridge) {
        window.VennAppsCartBridge.postAddCartLines(cartItems);
      }
      
      // Add bundle to cart using Shopify's cart API
      const cartData = { items: cartItems };
      const cartAddUrl = window.Shopify?.routes?.root ? 
        window.Shopify.routes.root + 'cart/add.js' : 
        '/cart/add.js';
      
      const addToCartResponse = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cartData)
      });
      
      if (!addToCartResponse.ok) {
        const errorText = await addToCartResponse.text();
        console.error('Cart add failed:', {
          status: addToCartResponse.status,
          statusText: addToCartResponse.statusText,
          error: errorText
        });
        throw new Error(`Failed to add bundle to cart (${addToCartResponse.status}): ${errorText}`);
      }
      
      const result = await addToCartResponse.json();
      console.log('Successfully added to cart:', result);
      return result;
      
    } catch (error) {
      console.error('Error creating Recharge bundle:', error);
      throw error;
    }
  }

  #getShippingRulesProperties(product) {
    const PRODUCT_TAGS = {
      ORIGINAL: ['Original', 'original'],
      FORAGE: ['Forage', 'forage'],
      ORIGINAL_BOX: 'original_box',
      FORAGE_BOX: 'forage-box',
      TESTER: 'Taster-box',
      STARTER: 'starter'
    };

    const properties = {};
    
    if (!product || !product.tags) return properties;
    
    const tags = Array.isArray(product.tags) ? product.tags : (typeof product.tags === 'string' ? product.tags.split(',').map(t => t.trim()) : []);
    
    let shippingType = '';
    
    if (tags.some(tag => PRODUCT_TAGS.ORIGINAL.includes(tag))) {
      shippingType = 'Original';
    } else if (tags.some(tag => PRODUCT_TAGS.FORAGE.includes(tag))) {
      shippingType = 'Forage';
    } else if (tags.includes(PRODUCT_TAGS.ORIGINAL_BOX)) {
      shippingType = 'original_box';
    } else if (tags.includes(PRODUCT_TAGS.FORAGE_BOX)) {
      shippingType = 'forage-box';
    } else if (tags.includes(PRODUCT_TAGS.TESTER)) {
      shippingType = 'Taster-box';
    } else if (tags.includes(PRODUCT_TAGS.STARTER)) {
      shippingType = 'starter';
    }
    
    if (shippingType) {
      properties._shipping_rules_type_product = shippingType;
      if (product.metafields?.custom?.number_of_items_in_the_box) {
        properties._shipping_rules_box_number = product.metafields.custom.number_of_items_in_the_box;
      }
    }
    
    return properties;
  }

  async addToCartOneTime(session) {
    const cartItems = [];
    
    // Add selected recipes
    if (session.selected_recipes) {
      session.selected_recipes.forEach(recipe => {
        const product = window.QuizProductData?.Recipes?.find(r => r.id === recipe.id);
        const baseProperties = {
          _quiz_type: 'recipe',
          _quiz_title: recipe.title,
          _quiz_id: session.quiz_id
        };
        const shippingProperties = this.#getShippingRulesProperties(product);
        
        cartItems.push({
          id: recipe.variant_id,
          product_id: product?.id,
          quantity: recipe.quantity,
          properties: { ...baseProperties, ...shippingProperties }
        });
      });
    }
    
    // Add selected treats
    if (session.selected_treats) {
      session.selected_treats.forEach(treat => {
        const product = window.QuizProductData?.Treats?.find(t => t.id === treat.id);
        if (product) {
          const baseProperties = {
            _quiz_type: 'treat',
            _quiz_title: treat.title,
            _quiz_id: session.quiz_id
          };
          const shippingProperties = this.#getShippingRulesProperties(product);
          
          cartItems.push({
            id: treat.variant_id || product.first_available_variant?.id,
            product_id: product.id,
            quantity: treat.quantity,
            properties: { ...baseProperties, ...shippingProperties }
          });
        }
      });
    }
    
    // Add selected supplements
    if (session.selected_supplements) {
      session.selected_supplements.forEach(supplement => {
        const product = window.QuizProductData?.Supplements?.find(s => s.id === supplement.id);
        if (product) {
          const baseProperties = {
            _quiz_type: 'supplement',
            _quiz_title: supplement.title,
            _quiz_id: session.quiz_id
          };
          const shippingProperties = this.#getShippingRulesProperties(product);
          
          cartItems.push({
            id: supplement.variant_id || product.first_available_variant?.id,
            product_id: product.id,
            quantity: supplement.quantity,
            properties: { ...baseProperties, ...shippingProperties }
          });
        }
      });
    }
    
    if (window.VennAppsCartBridge) {
      window.VennAppsCartBridge.postAddCartLines(cartItems);
    }

    // Add all items to cart
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ items: cartItems })
    });
    
    if (!response.ok) {
      throw new Error('Failed to add items to cart');
    }
    
    const result = await response.json();
    return result;
  }

  async updateAllCartContent() {
    try {
      // Define all sections that need to be updated
      const sectionsToUpdate = [
        { id: 'main-cart-items', section: 'main-cart-items', selector: '.shopify-section' },
        { id: 'main-cart-footer', section: 'main-cart-footer', selector: '.shopify-section' },
        { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }
      ];
      
      // Get current cart data
      const cartResponse = await fetch(`${routes.cart_url}.js`);
      const cartData = await cartResponse.json();
      
      // Build sections query
      const sectionIds = sectionsToUpdate.map(s => s.section).join(',');
      const base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const url = `${base}?sections=${encodeURIComponent(sectionIds)}&sections_url=${encodeURIComponent(window.location.pathname)}`;
      
      // Fetch updated sections
      const sectionsResponse = await fetch(url, { credentials: 'same-origin' });
      if (!sectionsResponse.ok) {
        throw new Error('Failed to fetch sections');
      }
      const sections = await sectionsResponse.json();
      
      // Update each section
      sectionsToUpdate.forEach(section => {
        const targetElement = document.getElementById(section.id);
        if (targetElement && sections[section.section]) {
          const parser = new DOMParser();
          const html = parser.parseFromString(sections[section.section], 'text/html');
          const sourceElement = html.querySelector(section.selector);
          if (sourceElement) {
            targetElement.innerHTML = sourceElement.innerHTML;
          }
        }
      });
      
      // Update cart icon specifically
      const cartIcons = document.querySelectorAll('.header__icon--cart');
      if (sections['cart-icon-bubble']) {
        const parser = new DOMParser();
        const html = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
        const sourceIcon = html.querySelector('.shopify-section');
        if (sourceIcon) {
          cartIcons.forEach(icon => {
            icon.innerHTML = sourceIcon.innerHTML;
          });
        }
      }
      
      // Update empty state classes
      const cartItems = document.querySelector('cart-items');
      const cartFooter = document.getElementById('main-cart-footer');
      const cartDrawer = document.querySelector('cart-drawer');
      
      const isEmpty = cartData.item_count === 0;
      if (cartItems) cartItems.classList.toggle('is-empty', isEmpty);
      if (cartFooter) cartFooter.classList.toggle('is-empty', isEmpty);
      if (cartDrawer) cartDrawer.classList.toggle('is-empty', isEmpty);
      
      // Publish cart update event
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'quiz-manager', cartData });
      }
      
    } catch (error) {
      console.error('Failed to update cart content:', error);
      
      // Fallback: try to update cart items only
      const cartItems = document.querySelector('cart-items');
      if (cartItems && cartItems.onCartUpdate) {
        await cartItems.onCartUpdate();
      }
    }
  }

  async #openAjaxCart() {
    try {
      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer) {
        return;
      }
      cartDrawer.classList.remove('is-empty');
      const sectionIds = cartDrawer
        .getSectionsToRender()
        .map((s) => s.id)
        .join(',');
      const base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const url = `${base}?sections=${encodeURIComponent(sectionIds)}&sections_url=${encodeURIComponent(window.location.pathname)}`;
      const res = await fetch(url, { credentials: 'same-origin' });
      if (!res.ok) return;
      const sections = await res.json();
      cartDrawer.renderContents({ sections });
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'quiz' });
      }
    } catch (e) {
      console.error('Failed to open Ajax cart', e);
    }
  }

  truncateText(text, maxLength) {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  }

  decodeHtmlEntities(text) {
    if (!text || typeof text !== 'string') return text || '';
    
    try {
      const textarea = document.createElement('textarea');
      textarea.innerHTML = text;
      return textarea.value;
    } catch (error) {
      console.error('Error in decodeHtmlEntities:', error, text);
      return text;
    }
  }

  showSuccessMessage() {
    const successMessage = document.getElementById('success-message');
    successMessage.classList.remove('hidden');
    setTimeout(() => {
      successMessage.classList.add('hidden');
    }, 1000);
    
  }

  showError(message) {
    // You can implement error toast notification here
    console.error(message);
  }

  async createCustomerWithQuizData(session) {
    if (!session.email_address) {
      console.warn('No email address provided, skipping customer creation');
      return;
    }

    try {
      const customerData = {
        email: session.email_address,
        firstName: this.extractFirstName(session),
        lastName: this.extractLastName(session),
        quizData: this.prepareQuizDataForWorker(session)
      };

      const workerUrl = (this.config && typeof this.config.worker_url === 'string' && this.config.worker_url.trim()) ? this.config.worker_url.trim() : '';
      
      const response = await fetch(workerUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(customerData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Worker error: ${errorData.error || 'Unknown error'}`);
      }

      const result = await response.json();
      console.log('Customer created/updated successfully:', result);
      
      return result;

    } catch (error) {
      console.error('Failed to create customer with quiz data:', error);
      // Don't throw error here to prevent cart addition failure
      // Customer creation is important but shouldn't block the purchase
    }
  }

  extractFirstName(session) {
    // Try to extract first name from dogs data or email
    if (session.dogs && session.dogs.length > 0) {
      const firstDogOwner = session.dogs[0].owner_name;
      if (firstDogOwner) {
        return firstDogOwner.split(' ')[0];
      }
    }
    
    // Fallback to email prefix
    const emailPrefix = session.email_address.split('@')[0];
    return emailPrefix.charAt(0).toUpperCase() + emailPrefix.slice(1);
  }

  extractLastName(session) {
    // Try to extract last name from dogs data
    if (session.dogs && session.dogs.length > 0) {
      const firstDogOwner = session.dogs[0].owner_name;
      if (firstDogOwner) {
        const nameParts = firstDogOwner.split(' ');
        return nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
      }
    }
    
    return '';
  }

  prepareQuizDataForWorker(session) {
    return {
      quiz_id: session.quiz_id,
      started_at: session.started_at,
      completed_at: new Date().toISOString(),
      current_step: session.current_step,
      
      // Dog information
      dogs: session.dogs || [],
      
      // Feeding preferences
      feeding_style: session.feeding_style || '',
      avoided_ingredients: session.avoided_ingredients || [],
      
      // Email preferences
      email_preferences: session.email_preferences || {
        recommendations: true,
        discounts: true,
        feeding_tips: true
      },
      
      // Pack and subscription details
      selected_pack_size: session.selected_pack_size || '',
      pack_details: session.pack_details || null,
      subscription_frequency: session.subscription_frequency || 'monthly',
      subscription_discount: session.subscription_discount || '',
      selected_selling_plan_id: session.selected_selling_plan_id || null,
      
      // Product selections
      selected_recipes: session.selected_recipes || [],
      selected_treats: session.selected_treats || [],
      selected_supplements: session.selected_supplements || [],
      total_meals_added: session.total_meals_added || 0,
      
      // Quiz completion status
      completed_steps: session.completed_steps || [],
      is_complete: session.current_step >= 9,
      
      // Additional metadata
      user_agent: navigator.userAgent,
      referrer: document.referrer,
      quiz_version: '1.0'
    };
  }
}

customElements.define('quiz-manager', NaturawQuizManager); 